const BASE_URL = process.env.REACT_APP_BASE_URL || "";

const getApiUrl = (
  endpoint: string,
  queryParams: Record<string, any> = {}
): string => {
  // Remove leading slash from endpoint if present
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  // Remove trailing slash from BASE_URL if present
  const cleanBaseUrl = BASE_URL.endsWith("/")
    ? BASE_URL.slice(0, -1)
    : BASE_URL;
  const url = `${cleanBaseUrl}/${cleanEndpoint}`;
  // Drop absent params. `new URLSearchParams({a: undefined})` serialises to the
  // literal string "a=undefined", which the API then rejects — an optional
  // query parameter left unset produced a 422 rather than being omitted.
  const present = Object.fromEntries(
    Object.entries(queryParams).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  );
  const queryString = new URLSearchParams(present).toString();
  return queryString ? `${url}?${queryString}` : url;
};

const BASE_PATHS = {
  AUTH: "api/v1/auth",
  KMS: "api/v1/kms",
  CUSTOMERS: "api/v1/customers",
  BRIEF_DOC: "api/v1/brief_doc",
  XFACTOR_BRIEFINGS: "api/v1/xfactor-briefings",
  USERS: "api/v1/users",
  RELATIONSHIP_ASSESSMENT: "api/v1/relationship-assessment",
  UPLOAD: "api/v1/upload",
  APPROVAL_REQUESTS: "api/v1/approval-requests",
  ASSIGNMENTS: "api/v1/assignments",
  ASSESSMENT_TEMPLATES: "api/v1/assessment-templates",
  ADMIN: "api/v1/admin",
  EXECUTIVE: "api/v1/executive",
  OPS: "api/v1/ops",
  NOTIFICATIONS: "api/v1/notifications",
  METRICS: "api/v1/metrics",
  ORG_TELEMETRICS: "api/v1/org-telemetrics",
  SIGNALS: "api/v1",
  TEAM_VIEWS: "api/v1/team-views",
};

const ENDPOINTS = {
  AUTH: {
    LOGIN: `${BASE_PATHS.AUTH}/login`,
    REFRESH_TOKEN: `${BASE_PATHS.AUTH}/refresh`,
    LOGOUT: `${BASE_PATHS.AUTH}/logout`,
    FORGOT_PASSWORD: `${BASE_PATHS.AUTH}/forgot-password`,
    RESET_PASSWORD: `${BASE_PATHS.AUTH}/reset-password`,
    CHANGE_PASSWORD: `${BASE_PATHS.AUTH}/change-password`,
  },
  KMS: {
    PUBLIC_KEY: `${BASE_PATHS.KMS}/public-key`,
  },

  CUSTOMERS: {
    BASE: BASE_PATHS.CUSTOMERS,
    BY_ID: (id: string) => `${BASE_PATHS.CUSTOMERS}/${id}`,
    ACCOUNT_TEAM: (id: string) => `${BASE_PATHS.CUSTOMERS}/${id}/account-team`,
    ACCOUNT_TEAM_BULK: (id: string) =>
      `${BASE_PATHS.CUSTOMERS}/${id}/account-team/bulk`,
    CUSTOMER_TEAM: (id: string) =>
      `${BASE_PATHS.CUSTOMERS}/${id}/customer-team`,
    CUSTOMER_TEAM_MEMBER: (customerId: string, userId: string) =>
      `${BASE_PATHS.CUSTOMERS}/${customerId}/customer-team/${userId}`,
    EXTRA_SPONSORS_DETAIL: (id: string) =>
      `${BASE_PATHS.CUSTOMERS}/${id}/extra-sponsors/detail`,
    EXTRA_SPONSORS: (id: string) =>
      `${BASE_PATHS.CUSTOMERS}/${id}/extra-sponsors`,
    EXTRA_SPONSOR_BY_ID: (customerId: string, id: string) =>
      `${BASE_PATHS.CUSTOMERS}/${customerId}/extra-sponsors/${id}`,
    ACCOUNT_TEAM_MEMBER_DEACTIVATE: (id: string) =>
      `${BASE_PATHS.CUSTOMERS}/account-team/${id}/submit-deactivate`,
    CUSTOMER_TEAM_MEMBER_DEACTIVATE: (id: string) =>
      `${BASE_PATHS.CUSTOMERS}/customer-team/${id}/submit-deactivate`,
    ACCOUNT_TEAM_MEMBER_UPDATE: (id: string) =>
      `${BASE_PATHS.CUSTOMERS}/account-team/${id}/submit-update`,
    STAKEHOLDER_RELATIONSHIPS: (id: string) =>
      `${BASE_PATHS.CUSTOMERS}/${id}/stakeholder-relationships`,
    BULK: `${BASE_PATHS.CUSTOMERS}/bulk`,
    ACTIVE_WITH_SPONSORS: `${BASE_PATHS.CUSTOMERS}/active-with-sponsors`,
    MONITOR_ACCOUNTS: `${BASE_PATHS.CUSTOMERS}/monitor-accounts`,
    EXECUTIVE_ACCOUNTS: `${BASE_PATHS.CUSTOMERS}/executive-accounts`,
    ACCOUNT_USERS: `${BASE_PATHS.CUSTOMERS}/account/users`,
    ASSIGNED_ACCOUNTS: `${BASE_PATHS.CUSTOMERS}/accounts/assigned`,
    ASSIGNED_PORTFOLIO_ACCOUNTS: `${BASE_PATHS.CUSTOMERS}/accounts/assigned/portfolio`,
    MY_PORTFOLIO_OVERVIEW: `${BASE_PATHS.CUSTOMERS}/accounts/my-portfolio/overview`,
    ACCOUNT_TEAM_CSV_VALIDATE: `${BASE_PATHS.CUSTOMERS}/account-team/csv/validate`,
    STAKEHOLDERS_EXPORT: `${BASE_PATHS.CUSTOMERS}/stakeholders/export`,
    WATCH_LISTS: `${BASE_PATHS.CUSTOMERS}/watch-lists`,
    CREATE_WATCH_LIST: `${BASE_PATHS.CUSTOMERS}/watch-lists`,
    DELETE_WATCH_LIST: (watchListId: string) =>
      `${BASE_PATHS.CUSTOMERS}/watch-lists/${watchListId}`,
    UPDATE_WATCH_LIST: (watchListId: string) =>
      `${BASE_PATHS.CUSTOMERS}/watch-lists/${watchListId}`,
  },

  BRIEF_DOC: {
    GENERATE_WATCH_LIST_BRIEFING: `${BASE_PATHS.BRIEF_DOC}/watch-list-briefing`,
  },

  USERS: {
    ORGANIZATION_USERS: `${BASE_PATHS.USERS}/organization-users`,
    ADMIN: `${BASE_PATHS.USERS}/admin`,
    ROLES: `${BASE_PATHS.USERS}/roles`,
    SPONSORS_WITH_ACCOUNTS: `${BASE_PATHS.USERS}/sponsors/with-accounts`,
    USERS_BULK: `${BASE_PATHS.USERS}/bulk`,
    DASHBOARD_COUNTS: `${BASE_PATHS.USERS}/dashboard/counts`,
    PROFILE_IMAGE_URL: `${BASE_PATHS.USERS}/profile/image-url`,
    SIDEBAR_PREFERENCES: `${BASE_PATHS.USERS}/profile/sidebar-preferences`,
    ORGANIZATION_SETTINGS: `${BASE_PATHS.USERS}/organization-settings`,
    PORTFOLIO_MAPS_CONFIG: `${BASE_PATHS.USERS}/portfolio-maps-config`,
  },

  RELATIONSHIP_ASSESSMENT: {
    STAKEHOLDERS: `${BASE_PATHS.RELATIONSHIP_ASSESSMENT}/stakeholders`,
    ACCOUNTS: `${BASE_PATHS.RELATIONSHIP_ASSESSMENT}/accounts`,
    ACCOUNT_OVERVIEW: (accountId: string) =>
      `${BASE_PATHS.RELATIONSHIP_ASSESSMENT}/accounts/${accountId}/overview`,
    ACCOUNT_ASSESSMENTS: (accountId: string) =>
      `${BASE_PATHS.RELATIONSHIP_ASSESSMENT}/accounts/${accountId}/assessments`,
    USER_MAPPINGS: `${BASE_PATHS.RELATIONSHIP_ASSESSMENT}/user-mappings`,
    RESOLVE_INHERITED: (accountId: string, userId: string, contactId: string) =>
      `${BASE_PATHS.RELATIONSHIP_ASSESSMENT}/accounts/${accountId}/assessments/${userId}/${contactId}/inherited`,
  },

  TEAM_VIEWS: {
    ROSTER: `${BASE_PATHS.TEAM_VIEWS}/roster`,
    ROLE_GAPS: `${BASE_PATHS.TEAM_VIEWS}/role-gaps`,
    PORTFOLIO_COVERAGE: `${BASE_PATHS.TEAM_VIEWS}/portfolio-coverage`,
    HISTORY: (personId: string) =>
      `${BASE_PATHS.TEAM_VIEWS}/history/${personId}`,
    REASSIGN_PREVIEW: `${BASE_PATHS.TEAM_VIEWS}/reassign/preview`,
    REASSIGN_APPLY: `${BASE_PATHS.TEAM_VIEWS}/reassign/apply`,
    DEPARTURE_IMPACT_APPLY: `${BASE_PATHS.TEAM_VIEWS}/departure-impact/apply`,
  },

  COVERAGE: {
    DEPARTED_ROWS: (accountId: string) =>
      `${BASE_PATHS.CUSTOMERS}/${accountId}/coverage/departed-rows`,
    SUCCESSOR_CANDIDATES: (accountId: string, departingUserId: string) =>
      `${BASE_PATHS.CUSTOMERS}/${accountId}/coverage/successor-candidates?departing_user_id=${departingUserId}`,
    DORMANT_HISTORY: (accountId: string) =>
      `${BASE_PATHS.CUSTOMERS}/${accountId}/coverage/dormant-history`,
    TRANSFER: (accountId: string) =>
      `${BASE_PATHS.CUSTOMERS}/${accountId}/coverage/transfer`,
    DISMISS: (accountId: string) =>
      `${BASE_PATHS.CUSTOMERS}/${accountId}/coverage/dismiss`,
    MAPPING_PREVIEW: (accountId: string) =>
      `${BASE_PATHS.CUSTOMERS}/${accountId}/coverage/mapping/preview`,
    MAPPING_APPLY: (accountId: string) =>
      `${BASE_PATHS.CUSTOMERS}/${accountId}/coverage/mapping/apply`,
  },

  UPLOAD: {
    IMAGE: `${BASE_PATHS.UPLOAD}/image`,
    BULK: "api/v1/bulk-upload",
  },
  APPROVAL_REQUESTS: {
    REQUEST: `${BASE_PATHS.APPROVAL_REQUESTS}/request`,
    APPROVE: `${BASE_PATHS.APPROVAL_REQUESTS}/approve`,
  },
  XFACTOR_BRIEFINGS: {
    ORGS: `${BASE_PATHS.XFACTOR_BRIEFINGS}/orgs`,
    WATCH_LIST_BRIEFING: `${BASE_PATHS.XFACTOR_BRIEFINGS}/watch-list-briefing`,
    ACCOUNT_BRIEFINGS: `${BASE_PATHS.XFACTOR_BRIEFINGS}/account-briefings`,
    ACCOUNT_PIPELINE_STATUS: `${BASE_PATHS.XFACTOR_BRIEFINGS}/account-pipeline-status`,
    DIGESTS: `${BASE_PATHS.XFACTOR_BRIEFINGS}/digests`,
    BATCH_PIPELINE: (batchId: string) =>
      `${BASE_PATHS.XFACTOR_BRIEFINGS}/batch/${batchId}/pipeline`,
    BATCH_RETRY_PIPELINE: (batchId: string) =>
      `${BASE_PATHS.XFACTOR_BRIEFINGS}/batch/${batchId}/retry-pipeline`,
    KPI_ACCOUNT_BRIEFINGS: `${BASE_PATHS.XFACTOR_BRIEFINGS}/kpi/account-briefings`,
    KPI_GENERATE: `${BASE_PATHS.XFACTOR_BRIEFINGS}/kpi/generate`,
    KPI_AVAILABILITY: `${BASE_PATHS.XFACTOR_BRIEFINGS}/kpi/availability`,
    KPI_METRICS_BREAKDOWN: `${BASE_PATHS.XFACTOR_BRIEFINGS}/kpi/metrics-breakdown`,
    KPI_SCORES_LATEST: `${BASE_PATHS.XFACTOR_BRIEFINGS}/kpi/scores/latest`,
  },
  ASSESSMENT_TEMPLATES: {
    BASE: BASE_PATHS.ASSESSMENT_TEMPLATES,
    BY_ID: (id: string) => `${BASE_PATHS.ASSESSMENT_TEMPLATES}/${id}`,
    CREATE: BASE_PATHS.ASSESSMENT_TEMPLATES,
    UPDATE: (id: string) => `${BASE_PATHS.ASSESSMENT_TEMPLATES}/${id}`,
    DELETE: (id: string) => `${BASE_PATHS.ASSESSMENT_TEMPLATES}/${id}`,
  },
  ASSESSMENTS: {
    BASE: "api/v1/assessments",
    CREATE: "api/v1/assessments",
    UPDATE: (id: string) => `api/v1/assessments/${id}`,
    UPDATE_STATUS: (id: string) => `api/v1/assessments/${id}/status`,
    PUBLISH: (id: string) => `api/v1/assessments/${id}/publish`,
    BULK_UPDATE_STATUS: "api/v1/assessments/status/bulk",
    DELETE: (id: string) => `api/v1/assessments/${id}`,
    RESPONSES: "api/v1/assessment-responses",
    RESPONSES_BY_ASSESSMENT: (id: string) =>
      `api/v1/assessments/${id}/responses`,
    QUESTIONS_AND_RESPONSES: (id: string) =>
      `api/v1/assessments/${id}/questions-and-responses`,
    OVERRIDE_ACCOUNT_LEVEL: (assessmentId: string, accountId: string) =>
      `api/v1/assessments/${assessmentId}/accounts/${accountId}/override-level`,
    LEVEL_MIX: "api/v1/assessments/level-mix",
    TRENDLINE: (accountId: string) =>
      `api/v1/assessments/${accountId}/trendline`,
    ACCOUNTS_TEAMS_BATCH: "api/v1/assessments/accounts/teams-batch",
    SELF: "api/v1/assessments/self",
    SELF_LATEST: "api/v1/assessments/self/latest",
    SELF_ASSESSMENTS_VENDOR: "api/v1/assessments/self-assessments",
    GET_ASSESSMENT_VALIDATION: "api/v1/assessments/get-assessment-validation",
    CHECK_SELF_RUNNING: "api/v1/assessments/check-self-running",
    CALENDAR_STATUS: "api/v1/assessments/calendar-status",
    CHECK_FORMAL_RUNNING: "api/v1/assessments/check-formal-running",
    DISCONNECT_REVIEW: "api/v1/assessments/disconnect-review",
    DISCONNECT_RESOLUTIONS: "api/v1/assessments/disconnect-resolutions",
    RECORD_RESOLUTION: "api/v1/assessments/record-resolution",
    PUBLISH_POST_RESOLUTION: "api/v1/assessments/publish-post-resolution",
    TRIGGER_DISCONNECT: "api/v1/assessments/trigger-disconnect",
  },
  UPLOAD_STATS: "api/v1/upload-stats",
  SELF_ASSESSMENT_CONFIG: {
    BASE: "api/v1/self-assessment-config",
    BY_ID: (id: string) => `api/v1/self-assessment-config/${id}`,
  },
  ASSIGNMENTS: {
    BASE: BASE_PATHS.ASSIGNMENTS,
    ACTIVATION_READY: "api/v1/assignments/activation-ready",
    OVERVIEW: "api/v1/assignments/overview",
    ACTIVATION_ACTION_BULK: "api/v1/assignments/activation-action/bulk",
    UNASSIGNED_CUSTOMERS: "api/v1/assignments/unassigned-customers",
    RECOMMEND_UNIFIED: `${BASE_PATHS.ASSIGNMENTS}/match/recommend-unified`,
    PENDING_APPROVALS: `${BASE_PATHS.ASSIGNMENTS}/requests/pending-approvals`,
    APPROVE_REQUEST: `${BASE_PATHS.ASSIGNMENTS}/approve`,
    EDIT_REQUEST: `${BASE_PATHS.ASSIGNMENTS}/requests/edit`,
  },
  ADMIN: {
    BASE: BASE_PATHS.ADMIN,
    STATISTICS: `${BASE_PATHS.ADMIN}/statistics`,
    ALL_ORGANIZATIONS: `${BASE_PATHS.ADMIN}/organizations`,
    ALL_USERS: `${BASE_PATHS.ADMIN}/users`,
    ACTIVE_USERS: (status: string) =>
      `${BASE_PATHS.ADMIN}/users?status=${status}`,
    CREATE_TENANT: "api/v1/tenant",
    ASSESSMENT_TEMPLATES: `${BASE_PATHS.ADMIN}/assessment-templates`,
    AUTH_LOGS: `${BASE_PATHS.ADMIN}/auth-logs`,
    SEND_EMAIL: `${BASE_PATHS.ADMIN}/emails/send`,
    ROLES: `${BASE_PATHS.ADMIN}/roles`,
    CHANGE_MY_PASSWORD: `${BASE_PATHS.ADMIN}/change-my-password`,
    VIDEO_URLS: `${BASE_PATHS.ADMIN}/video-urls`,
    VIDEO_URL_BY_ID: (id: string) => `${BASE_PATHS.ADMIN}/video-urls/${id}`,
    VIDEO_LIBRARY: `${BASE_PATHS.ADMIN}/video-library`,
    VIDEO_LIBRARY_REORDER: `${BASE_PATHS.ADMIN}/video-library/reorder`,
    VIDEO_LIBRARY_BY_ID: (id: string) =>
      `${BASE_PATHS.ADMIN}/video-library/${id}`,
    VIDEO_ASSIGN: `${BASE_PATHS.ADMIN}/video-urls/assign`,
    VIDEO_TOPICS: `${BASE_PATHS.ADMIN}/video-topics`,
    VIDEO_TOPIC_BY_ID: (id: string) => `${BASE_PATHS.ADMIN}/video-topics/${id}`,
    SPONSOR_TIPS: `${BASE_PATHS.ADMIN}/guidance-library`,
    SPONSOR_TIP_BY_ID: (id: string) =>
      `${BASE_PATHS.ADMIN}/guidance-library/${id}`,
    HEALTH_CHECK: `${BASE_PATHS.ADMIN}/health-check`,
    HEALTH_CHECK_REPAIR: `${BASE_PATHS.ADMIN}/health-check/repair`,
    IB_HEALTH_CHECK: `${BASE_PATHS.ADMIN}/ib-health-check`,
    IB_HEALTH_CHECK_REPAIR: `${BASE_PATHS.ADMIN}/ib-health-check/repair`,
    IB_HEALTH_CHECK_REPAIR_STOP: `${BASE_PATHS.ADMIN}/ib-health-check/repair/stop`,
    BUSINESS_HEALTH_BRIEFINGS_SYNC: `${BASE_PATHS.ADMIN}/business-health-briefings/sync`,
    BUSINESS_HEALTH_BRIEFINGS_ACCOUNTS: `${BASE_PATHS.ADMIN}/business-health-briefings/accounts`,
    DATA_INTEGRITY_CHECK: `${BASE_PATHS.ADMIN}/data-integrity-check`,
    METADATA_INFO: `${BASE_PATHS.ADMIN}/metadata-info`,
    RELATIONSHIP_LEVELS: `${BASE_PATHS.ADMIN}/relationship-levels`,
    RELATIONSHIP_LEVEL_BY_ID: (id: string) =>
      `${BASE_PATHS.ADMIN}/relationship-levels/${id}`,
    ORGANIZATION_RL_SETTING_CONFIG: (tenantId: string) =>
      `${BASE_PATHS.ADMIN}/organizations/${tenantId}/settings/rl_setting_config`,
    ORGANIZATION_WEEKLY_DIGEST: (tenantId: string) =>
      `${BASE_PATHS.ADMIN}/organizations/${tenantId}/settings/weekly_digest`,
    METRICS_CONFIG_DEFINITIONS: `${BASE_PATHS.ADMIN}/metrics-config/definitions`,
    METRICS_CONFIG_DEFINITION: (metricId: string) =>
      `${BASE_PATHS.ADMIN}/metrics-config/definitions/${metricId}`,
    METRICS_CONFIG_CATEGORIES: `${BASE_PATHS.ADMIN}/metrics-config/categories`,
    METRICS_CONFIG_WEIGHTAGE: `${BASE_PATHS.ADMIN}/metrics-config/weightage`,
    METRICS_CONFIG_WEIGHTAGE_ITEM: (configId: string) =>
      `${BASE_PATHS.ADMIN}/metrics-config/weightage/${configId}`,
    METRICS_CONFIG_WEIGHTAGE_ACTIVATE: `${BASE_PATHS.ADMIN}/metrics-config/weightage/activate`,
    METRICS_CONFIG_RESET: `${BASE_PATHS.ADMIN}/metrics-config/reset`,
    METRICS_CONFIG_DATASETS: `${BASE_PATHS.ADMIN}/metrics-config/datasets`,
    METRICS_CONFIG_RUNS: `${BASE_PATHS.ADMIN}/metrics-config/runs`,
    METRICS_CONFIG_RUN: (runId: string) =>
      `${BASE_PATHS.ADMIN}/metrics-config/runs/${runId}`,
    METRICS_CONFIG_QA_CHECKS: `${BASE_PATHS.ADMIN}/metrics-config/qa/checks`,
    METRICS_CONFIG_QA_CHECK: (checkKey: string) =>
      `${BASE_PATHS.ADMIN}/metrics-config/qa/checks/${checkKey}`,
    METRICS_CONFIG_QA_RUN: `${BASE_PATHS.ADMIN}/metrics-config/qa/run`,
    METRICS_CONFIG_QA_RUNS: `${BASE_PATHS.ADMIN}/metrics-config/qa/runs`,
    METRICS_CONFIG_QA_RUN_DETAIL: (runId: string) =>
      `${BASE_PATHS.ADMIN}/metrics-config/qa/runs/${runId}`,
    ORGANIZATION_PRODUCT_TELEMETRICS: (tenantId: string) =>
      `${BASE_PATHS.ADMIN}/organizations/${tenantId}/settings/product_telemetrics`,
    ORGANIZATION_COVERAGE_ANALYSIS: (tenantId: string) =>
      `${BASE_PATHS.ADMIN}/organizations/${tenantId}/settings/coverage_analysis`,
    PORTFOLIO_MAPS_CONFIG: `${BASE_PATHS.ADMIN}/portfolio-maps-config`,
    RELEASE_NOTES: `${BASE_PATHS.ADMIN}/release-notes`,
    RELEASE_NOTES_VERSION: (versionId: string) =>
      `${BASE_PATHS.ADMIN}/release-notes/${versionId}`,
    RELEASE_NOTES_RECIPIENTS: `${BASE_PATHS.ADMIN}/release-notes/recipients`,
    RELEASE_NOTES_PREVIEW: `${BASE_PATHS.ADMIN}/release-notes/preview`,
    RELEASE_NOTES_SEND: `${BASE_PATHS.ADMIN}/release-notes/send`,
    CRON_SETTINGS: `${BASE_PATHS.ADMIN}/cron-settings`,
    AL_ASSESSMENT_DEFAULTS: `${BASE_PATHS.ADMIN}/al-assessment-defaults`,
    AL_ASSESSMENT_TEST_MODE: `${BASE_PATHS.ADMIN}/al-assessment-test-mode`,
    AL_ASSESSMENT_TEST_MODE_RUN: `${BASE_PATHS.ADMIN}/al-assessment-test-mode/run`,
    AL_ASSESSMENT_TEST_MODE_RESET: `${BASE_PATHS.ADMIN}/al-assessment-test-mode/reset`,
    SIGNAL_ADMIN_ORGANIZATIONS: `${BASE_PATHS.ADMIN}/signal-administration/organizations`,
    SIGNAL_ADMIN_PIPELINE_HEALTH: `${BASE_PATHS.ADMIN}/signal-administration/pipeline-health`,
    SIGNAL_ADMIN_PIPELINE_STATUS: `${BASE_PATHS.ADMIN}/signal-administration/pipeline-status`,
    SIGNAL_ADMIN_RUN_SWEEP: `${BASE_PATHS.ADMIN}/signal-administration/pipeline/run-sweep`,
    SIGNAL_ADMIN_PLATFORM_CONFIG: `${BASE_PATHS.ADMIN}/signal-administration/config`,
    SIGNAL_ADMIN_PLATFORM_CONFIG_PREVIEW: `${BASE_PATHS.ADMIN}/signal-administration/config/preview`,
    SIGNAL_ADMIN_ACTION_PLAN_REVIEWS: `${BASE_PATHS.ADMIN}/signal-administration/config/action-plans/reviews`,
    SIGNAL_ADMIN_PLATFORM_AUDIT: `${BASE_PATHS.ADMIN}/signal-administration/audit`,
    SIGNAL_ADMIN_PLATFORM_REGISTRY: `${BASE_PATHS.ADMIN}/signal-administration/registry`,
    SIGNAL_ADMIN_RESTORE: `${BASE_PATHS.ADMIN}/signal-administration/config/restore`,
    SIGNAL_ADMIN_RESTORE_PREVIEW: (version: number) =>
      `${BASE_PATHS.ADMIN}/signal-administration/config/restore/${version}/preview`,
    SIGNAL_ADMIN_CONFIG: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/config`,
    SIGNAL_ADMIN_AUDIT: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/audit`,
    SIGNAL_ADMIN_OVERVIEW: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/overview`,
    SIGNAL_ADMIN_PRESENTATION: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/presentation`,
    SIGNAL_ADMIN_PRESENTATION_PREVIEW: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/presentation/preview`,
    SIGNAL_ADMIN_PRESENTATION_CLEAR: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/presentation/clear`,
    SIGNAL_ADMIN_CONFIG_PREVIEW: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/config/preview`,
    SIGNAL_ADMIN_REGISTRY: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/registry`,
    SIGNAL_ADMIN_TRACES: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/traces`,
    SIGNAL_ADMIN_RUNS: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/runs`,
    SIGNAL_ADMIN_CLASSIFIER_IMPACT: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/classifier/impact`,
    SIGNAL_ADMIN_CLASSIFIER_TRY: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/classifier/try`,
    SIGNAL_ADMIN_RUN_DIFF: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/run-diff`,
    SIGNAL_ADMIN_ACTION_HISTORY: (orgId: string) =>
      `${BASE_PATHS.ADMIN}/signal-administration/organizations/${orgId}/action-history`,
    GUIDANCE_HUB_SETTINGS: (key: string) =>
      `${BASE_PATHS.ADMIN}/guidance-hub/settings/${key}`,
    GUIDANCE_HUB_NAV_LINKS: `${BASE_PATHS.ADMIN}/guidance-hub/nav-links`,
    GUIDANCE_HUB_NAV_LINK_BY_KEY: (key: string) =>
      `${BASE_PATHS.ADMIN}/guidance-hub/nav-links/${key}`,
    GUIDANCE_HUB_PLAYBOOKS: `${BASE_PATHS.ADMIN}/guidance-hub/playbooks`,
    GUIDANCE_HUB_PLAYBOOK_CELL: (role: string, cadence: string) =>
      `${BASE_PATHS.ADMIN}/guidance-hub/playbooks/${role}/${cadence}`,
    GUIDANCE_HUB_RHYTHM_PROFILES: `${BASE_PATHS.ADMIN}/guidance-hub/rhythm/profiles`,
    GUIDANCE_HUB_RHYTHM_PROFILE: (role: string) =>
      `${BASE_PATHS.ADMIN}/guidance-hub/rhythm/profiles/${role}`,
    GUIDANCE_HUB_RHYTHM_CADENCES: `${BASE_PATHS.ADMIN}/guidance-hub/rhythm/cadences`,
    GUIDANCE_HUB_RHYTHM_CADENCE: (role: string, cadence: string) =>
      `${BASE_PATHS.ADMIN}/guidance-hub/rhythm/cadences/${role}/${cadence}`,
  },
  GUIDANCE_HUB: {
    SETTINGS: "api/v1/guidance-hub/settings",
    NAV_LINKS: "api/v1/guidance-hub/nav-links",
    PLAYBOOKS: "api/v1/guidance-hub/playbooks",
    PLAYBOOKS_ME: "api/v1/guidance-hub/playbooks/me",
    RHYTHM_PROFILES: "api/v1/guidance-hub/rhythm/profiles",
    RHYTHM_CADENCES: "api/v1/guidance-hub/rhythm/cadences",
    RHYTHM_BUNDLE: (role: string) => `api/v1/guidance-hub/rhythm/${role}`,
  },
  EXECUTIVE: {
    REVIEW_ASSESSMENTS: `${BASE_PATHS.EXECUTIVE}/review-assessments`,
  },
  SPONSOR_ENGAGEMENT: {
    BASE: "api/v1/sponsor-engagement",
    CONFIG: "api/v1/sponsor-engagement/config",
    CYCLES: "api/v1/sponsor-engagement/cycles",
    CYCLE_RESPONSES: (cycleId: string) =>
      `api/v1/sponsor-engagement/cycles/${cycleId}/responses`,
    CYCLE_LEADERBOARD: (cycleId: string) =>
      `api/v1/sponsor-engagement/cycles/${cycleId}/leaderboard`,
    LAUNCH: "api/v1/sponsor-engagement/cycles/launch",
    EXTEND_DUE_DATE: (cycleId: string) =>
      `api/v1/sponsor-engagement/cycles/${cycleId}/due-date`,
    LAUNCH_CONTEXT: "api/v1/sponsor-engagement/launch-context",
    DISCOVERY_HUB: "api/v1/sponsor-engagement/discovery-hub",
    PORTFOLIO: "api/v1/sponsor-engagement/portfolio",
    TEAM_FEEDBACK: "api/v1/sponsor-engagement/team-feedback/pending",
    MY_PERFORMANCE: "api/v1/sponsor-engagement/my-performance",
    LEADERBOARD: "api/v1/sponsor-engagement/leaderboard",
    SAVE_DRAFT_LEADERBOARD: "api/v1/sponsor-engagement/leaderboard/save-draft",
    SUBMIT_APPROVAL:
      "api/v1/sponsor-engagement/leaderboard/submit-for-approval",
    CHANGE_APPROVER: "api/v1/sponsor-engagement/leaderboard/change-approver",
    APPROVE: "api/v1/sponsor-engagement/leaderboard/approve",
    SEND_BACK: "api/v1/sponsor-engagement/leaderboard/send-back",
    EXECUTIVE_SUMMARY: "api/v1/sponsor-engagement/executive-summary",
    POLL1: (accountId: string) =>
      `api/v1/sponsor-engagement/assignments/${accountId}/poll1`,
    POLL1_DRAFT: (accountId: string) =>
      `api/v1/sponsor-engagement/assignments/${accountId}/poll1/draft`,
    POLL2: (accountId: string) =>
      `api/v1/sponsor-engagement/assignments/${accountId}/poll2`,
    POLL2_DRAFT: (accountId: string) =>
      `api/v1/sponsor-engagement/assignments/${accountId}/poll2/draft`,
    REMINDER: (sponsorId: string) =>
      `api/v1/sponsor-engagement/reminders/sponsor/${sponsorId}`,
    POLL2_REMINDER: (teamMemberId: string) =>
      `api/v1/sponsor-engagement/reminders/team-member/${teamMemberId}`,
    BULK_REMINDERS: "api/v1/sponsor-engagement/reminders/bulk",
    DRILLDOWN: (sponsorId: string) =>
      `api/v1/sponsor-engagement/sponsors/${sponsorId}/drilldown`,
  },
  SPONSOR_TIPS: {
    BASE: "api/v1/guidance-library",
    RECOMMENDED: "api/v1/guidance-library/recommended",
    BOOKMARKS: "api/v1/guidance-library/bookmarks",
    BOOKMARK_BY_SLUG: (slug: string) =>
      `api/v1/guidance-library/${slug}/bookmark`,
    BY_SLUG: (slug: string) => `api/v1/guidance-library/${slug}`,
  },
  OPS: {
    COMMAND_CENTER_METRICS: `${BASE_PATHS.OPS}/command-center/metrics`,
    COMMAND_CENTER_ACCOUNTS_NEED_SPONSORS: `${BASE_PATHS.OPS}/command-center/accounts-need-sponsors`,
    COMMAND_CENTER_SPONSORS_ON_BENCH: `${BASE_PATHS.OPS}/command-center/sponsors-on-bench`,
    COMMAND_CENTER_ACCOUNT_SURVEYS: `${BASE_PATHS.OPS}/command-center/account-surveys`,
  },
  METRICS: {
    IMPORT_CSV: `${BASE_PATHS.METRICS}/import/csv`,
    IMPORT_CSV_VALIDATE: `${BASE_PATHS.METRICS}/import/csv/validate`,
    STAGING_PROCESS: `${BASE_PATHS.METRICS}/staging/process`,
    STAGING_TRANSFER: `${BASE_PATHS.METRICS}/staging/transfer`,
    STAGING_COUNT: `${BASE_PATHS.METRICS}/staging/count`,
    NORMALIZE: `${BASE_PATHS.METRICS}/normalize`,
    NORMALIZE_PENDING_COUNT: `${BASE_PATHS.METRICS}/normalize/pending-count`,
    SUMMARY_TRANSFER: `${BASE_PATHS.METRICS}/summary/transfer`,
    SUMMARY_STATUS: `${BASE_PATHS.METRICS}/summary/transfer/status`,
    ORG_CONFIGURATIONS: `${BASE_PATHS.METRICS}/org-configurations`,
    BENCHMARKS_CALCULATE: `${BASE_PATHS.METRICS}/benchmarks/calculate`,
    BENCHMARKS_PROPAGATE: `${BASE_PATHS.METRICS}/benchmarks/propagate`,
    HAWKEYE_DIGEST_DB: `${BASE_PATHS.METRICS}/hawkeye-digest-db`,
    TIME_PERIODS: `${BASE_PATHS.METRICS}/time-periods`,
    CONFIG_COVERAGE_AUDIT: `${BASE_PATHS.METRICS}/audit/config-coverage`,
    // Data Hub run bookkeeping
    PIPELINE_RUNS: `${BASE_PATHS.METRICS}/pipeline/runs`,
    DATAHUB_STATS: `${BASE_PATHS.METRICS}/datahub/stats`,
    DATAHUB_DATASETS: `${BASE_PATHS.METRICS}/datahub/datasets`,
    DATAHUB_FILES: `${BASE_PATHS.METRICS}/datahub/files`,
    DATAHUB_REPORTS: `${BASE_PATHS.METRICS}/datahub/reports`,
    DATAHUB_FILE_DOWNLOAD: `${BASE_PATHS.METRICS}/datahub/files/download`,
    DATAHUB_LEDGER_RUNS: `${BASE_PATHS.METRICS}/datahub/ledger/runs`,
    DATAHUB_LEDGER_RUN: (runId: string) =>
      `${BASE_PATHS.METRICS}/datahub/ledger/runs/${runId}`,
    DATAHUB_LEDGER_DECISIONS: (runId: string) =>
      `${BASE_PATHS.METRICS}/datahub/ledger/runs/${runId}/decisions`,
    DATAHUB_LEDGER_KILL: (runId: string) =>
      `${BASE_PATHS.METRICS}/datahub/ledger/runs/${runId}/kill`,
    DATAHUB_LEDGER_STEP_BEGIN: (runId: string, step: string) =>
      `${BASE_PATHS.METRICS}/datahub/ledger/runs/${runId}/steps/${step}/begin`,
    DATAHUB_LEDGER_STEP_SUCCEED: (runId: string, step: string) =>
      `${BASE_PATHS.METRICS}/datahub/ledger/runs/${runId}/steps/${step}/succeed`,
    DATAHUB_STAGING_ROWS: `${BASE_PATHS.METRICS}/datahub/staging-rows`,
    DATAHUB_ACCOUNTS: `${BASE_PATHS.METRICS}/datahub/accounts`,
    DATAHUB_METRIC_DEFINITIONS: `${BASE_PATHS.METRICS}/datahub/metric-definitions`,
    DATAHUB_QC_FILE_REPORT: `${BASE_PATHS.METRICS}/datahub/qc-file-report`,
    DATAHUB_QA: `${BASE_PATHS.METRICS}/datahub/qa`,
    DATAHUB_IMPORT_TEMPLATE: `${BASE_PATHS.METRICS}/datahub/import-template`,
    DATAHUB_REJECTED_ROWS_CSV: `${BASE_PATHS.METRICS}/datahub/rejected-rows.csv`,
    DATAHUB_RESET_TARGETS: `${BASE_PATHS.METRICS}/datahub/reset/targets`,
    DATAHUB_RESET_PREVIEW: `${BASE_PATHS.METRICS}/datahub/reset/preview`,
    DATAHUB_RESET: `${BASE_PATHS.METRICS}/datahub/reset`,
    DATAHUB_PRODUCTION_ROWS: `${BASE_PATHS.METRICS}/datahub/production-rows`,
    DATAHUB_BENCHMARKS: `${BASE_PATHS.METRICS}/datahub/benchmarks`,
    DATAHUB_MATRIX: `${BASE_PATHS.METRICS}/datahub/matrix`,
    SCORES_CATEGORY: `${BASE_PATHS.METRICS}/scores/category`,
    RO_COMPUTE: `${BASE_PATHS.METRICS}/ro/compute`,
    RO_CONFIG: `${BASE_PATHS.METRICS}/ro/config`,
    RO_CONFIG_RESET: `${BASE_PATHS.METRICS}/ro/config/reset`,
    QC_SUMMARY: `${BASE_PATHS.METRICS}/qc/summary`,
    QC_ISSUES: `${BASE_PATHS.METRICS}/qc/issues`,
    QC_RESOLVE: `${BASE_PATHS.METRICS}/qc/resolve`,
    DATAHUB_CUSTOM_METRICS: `${BASE_PATHS.METRICS}/datahub/custom-metrics`,
    DATAHUB_CUSTOM_CATEGORIES: `${BASE_PATHS.METRICS}/datahub/custom-categories`,
    DATAHUB_CUSTOM_RUBRIC: `${BASE_PATHS.METRICS}/datahub/custom-rubric`,
    DATAHUB_CUSTOM_RUBRIC_IMPORT: `${BASE_PATHS.METRICS}/datahub/custom-rubric/import`,
    QC_CONFLICTS: `${BASE_PATHS.METRICS}/qc/conflicts`,
    QC_CONFLICTS_RESOLVE: `${BASE_PATHS.METRICS}/qc/conflicts/resolve`,
    QC_ACCEPT_ALL: `${BASE_PATHS.METRICS}/qc/accept-all`,
    QC_REJECT_ALL: `${BASE_PATHS.METRICS}/qc/reject-all`,
    QC_RUN: `${BASE_PATHS.METRICS}/qc/run`,
    SUMMARIZE_ROLLUP: `${BASE_PATHS.METRICS}/summarize/rollup`,
    QC_PROBES: `${BASE_PATHS.METRICS}/qc/probes`,
    PIPELINE_RUN: (runId: string) =>
      `${BASE_PATHS.METRICS}/pipeline/runs/${runId}`,
    PIPELINE_RUN_CLOSE: (runId: string) =>
      `${BASE_PATHS.METRICS}/pipeline/runs/${runId}/close`,
    PIPELINE_RUN_PHASE: (runId: string, phase: string) =>
      `${BASE_PATHS.METRICS}/pipeline/runs/${runId}/phases/${phase}`,
  },
  ORG_TELEMETRICS: {
    BASE: BASE_PATHS.ORG_TELEMETRICS,
    USERS: `${BASE_PATHS.ORG_TELEMETRICS}/users`,
  },
  SIGNALS: {
    FEED: `${BASE_PATHS.SIGNALS}/signal-feed`,
    PRESENTATION: `${BASE_PATHS.SIGNALS}/signal-presentation`,
    SNOOZE: (firingId: string) =>
      `${BASE_PATHS.SIGNALS}/signal-firings/${firingId}/snooze`,
    DISMISS: (firingId: string) =>
      `${BASE_PATHS.SIGNALS}/signal-firings/${firingId}/dismiss`,
    GUIDE_STEP: (firingId: string) =>
      `${BASE_PATHS.SIGNALS}/signal-firings/${firingId}/guide-step`,
    CTA_CLICK: (firingId: string) =>
      `${BASE_PATHS.SIGNALS}/signal-firings/${firingId}/cta-click`,
    PORTFOLIO_MAP: `${BASE_PATHS.SIGNALS}/portfolio-map`,
    HISTORY_SUMMARY: `${BASE_PATHS.SIGNALS}/signal-history/summary`,
    ACTION_HISTORY: `${BASE_PATHS.SIGNALS}/action-history`,
    ACCOUNT_HISTORY: (accountId: string) =>
      `${BASE_PATHS.SIGNALS}/accounts/${accountId}/signal-history`,
  },
  VENDOR_ADMIN: {
    STATISTICS: "api/v1/vendor-admin/statistics",
    ASSIGNMENT_RECOMMENDATIONS:
      "api/v1/vendor-admin/assignment-recommendations",
    ASSIGNMENT_RECOMMENDATIONS_HISTORY:
      "api/v1/vendor-admin/assignment-recommendations/history",
  },
  NOTIFICATIONS: {
    SETTINGS: `${BASE_PATHS.NOTIFICATIONS}/settings`,
    BASE: BASE_PATHS.NOTIFICATIONS,
    MARK_READ: `${BASE_PATHS.NOTIFICATIONS}/read`,
    BY_ID: (id: string) => `${BASE_PATHS.NOTIFICATIONS}/${id}`,
  },
  COLOR_CODING: {
    THRESHOLDS: "api/v1/vendor-admin/color-coding-thresholds",
  },
  VENDOR_CONFIG: {
    BASE: "api/v1/vendor-config",
    ACCOUNT_TEAM_ROLE_LIMITS: "api/v1/vendor-config/account_team_role_limites",
    EASY_ONBOARDING: "api/v1/vendor-config/easy_onbording",
    DISCONNECT_FLOW: "api/v1/vendor-config/disconnect_flow",
    PORTFOLIO_NEEDS_ATTENTION: "api/v1/vendor-config/portfolio_needs_attention",
    RL_SETTING_CONFIG: "api/v1/vendor-config/rl_setting_config",
    LOW_SCORE_CRITERIA: "api/v1/vendor-config/low_score_criteria",
    HEALTH_SCORE_THRESHOLDS: "api/v1/vendor-config/health_score_thresholds",
    SIGNAL_ENGINE_THRESHOLDS: "api/v1/vendor-config/signal_engine_thresholds",
    PRIORITY_SCORING_WEIGHTS: "api/v1/vendor-config/priority_scoring_weights",
    WEEKLY_DIGEST: "api/v1/vendor-config/weekly_digest",
    WEEKLY_DIGEST_STATUS: "api/v1/vendor-config/weekly_digest/status",
    WEEKLY_DIGEST_SPONSORS: "api/v1/vendor-config/weekly_digest/sponsors",
    WEEKLY_DIGEST_SEND_TEST: "api/v1/vendor-config/weekly_digest/send-test",
  },
  INVITATIONS: {
    LIST: "api/v1/invitations/list",
    SEND: "api/v1/invitations/send",
    SEND_PORTFOLIO: "api/v1/invitations/send-portfolio",
    REMIND: "api/v1/invitations/remind",
  },
  ONBOARDING_WIZARD: {
    VERIFY: "onboard",
    ACCOUNTS: "onboard/accounts",
    COMPLETE: "onboarding/complete",
  },
};

export const API_URLS = {
  // Auth
  login: (params: Record<string, any> = {}) =>
    getApiUrl(ENDPOINTS.AUTH.LOGIN, params),
  refreshToken: () => getApiUrl(ENDPOINTS.AUTH.REFRESH_TOKEN),
  logout: () => getApiUrl(ENDPOINTS.AUTH.LOGOUT),
  forgotPassword: () => getApiUrl(ENDPOINTS.AUTH.FORGOT_PASSWORD),
  resetPassword: () => getApiUrl(ENDPOINTS.AUTH.RESET_PASSWORD),
  changePassword: () => getApiUrl(ENDPOINTS.AUTH.CHANGE_PASSWORD),
  getKmsPublicKey: () => getApiUrl(ENDPOINTS.KMS.PUBLIC_KEY),

  // Customers
  getCustomerById: (customerId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.BY_ID(customerId)),
  getCustomerAccountTeam: (customerId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.ACCOUNT_TEAM(customerId)),
  createCustomerAccountTeamBulk: (customerId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.ACCOUNT_TEAM_BULK(customerId)),
  getCustomerTeam: (customerId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.CUSTOMER_TEAM(customerId)),
  createCustomerTeamMember: (customerId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.CUSTOMER_TEAM(customerId)),
  updateCustomerTeamMemberById: (customerId: string, userId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.CUSTOMER_TEAM_MEMBER(customerId, userId)),
  getCustomerExtraSponsorsDetail: (customerId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.EXTRA_SPONSORS_DETAIL(customerId)),
  createCustomerExtraSponsors: (customerId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.EXTRA_SPONSORS(customerId)),
  deleteCustomerExtraSponsor: (customerId: string, id: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.EXTRA_SPONSOR_BY_ID(customerId, id)),
  deactivateAccountTeamMember: (memberId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.ACCOUNT_TEAM_MEMBER_DEACTIVATE(memberId)),
  deactivateCustomerTeamMember: (memberId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.CUSTOMER_TEAM_MEMBER_DEACTIVATE(memberId)),
  updateAccountTeamMember: (memberId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.ACCOUNT_TEAM_MEMBER_UPDATE(memberId)),
  getStakeholderRelationships: (customerId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.STAKEHOLDER_RELATIONSHIPS(customerId)),
  createAccountsBulk: () => getApiUrl(ENDPOINTS.CUSTOMERS.BULK),
  activeCustomersWithSponsors: (params: Record<string, any> = {}) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.ACTIVE_WITH_SPONSORS, params),
  getAccountUsers: (params: Record<string, any> = {}) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.ACCOUNT_USERS, params),
  getAssignedAccounts: (params: Record<string, any> = {}) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.ASSIGNED_ACCOUNTS, params),
  getPortfolioAccounts: (params: Record<string, any> = {}) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.ASSIGNED_PORTFOLIO_ACCOUNTS, params),
  getMyPortfolioOverview: (params: Record<string, any> = {}) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.MY_PORTFOLIO_OVERVIEW, params),
  validateAccountTeamCsv: () =>
    getApiUrl(ENDPOINTS.CUSTOMERS.ACCOUNT_TEAM_CSV_VALIDATE),
  exportStakeholders: (format: "csv" | "excel" = "csv", scope = "all") =>
    getApiUrl(ENDPOINTS.CUSTOMERS.STAKEHOLDERS_EXPORT, { format, scope }),
  getAccountSummary: (accountId: string) =>
    getApiUrl(`${ENDPOINTS.CUSTOMERS.BASE}/${accountId}`),
  monitorAccounts: () => getApiUrl(ENDPOINTS.CUSTOMERS.MONITOR_ACCOUNTS),
  executiveAccounts: () => getApiUrl(ENDPOINTS.CUSTOMERS.EXECUTIVE_ACCOUNTS),
  customerWatchLists: (filters?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.WATCH_LISTS, filters),
  createWatchList: () => getApiUrl(ENDPOINTS.CUSTOMERS.CREATE_WATCH_LIST),
  deleteWatchList: (watchListId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.DELETE_WATCH_LIST(watchListId)),
  updateWatchList: (watchListId: string) =>
    getApiUrl(ENDPOINTS.CUSTOMERS.UPDATE_WATCH_LIST(watchListId)),

  // Brief Document (watchlist briefing)
  generateWatchListBriefing: () =>
    getApiUrl(ENDPOINTS.BRIEF_DOC.GENERATE_WATCH_LIST_BRIEFING),

  // Users
  getOrganizationUsers: (params?: { roles?: string }) =>
    getApiUrl(ENDPOINTS.USERS.ORGANIZATION_USERS, params || {}),
  getAdminUsers: () => getApiUrl(ENDPOINTS.USERS.ADMIN),
  getRoles: () => getApiUrl(ENDPOINTS.USERS.ROLES),
  getAdminRoles: () => getApiUrl(ENDPOINTS.ADMIN.ROLES),
  executiveSponsorsWithAccounts: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.USERS.SPONSORS_WITH_ACCOUNTS, params || {}),
  createUsersBulk: () => getApiUrl(ENDPOINTS.USERS.USERS_BULK),
  getDashboardCounts: () => getApiUrl(ENDPOINTS.USERS.DASHBOARD_COUNTS),
  userProfileImageUrl: () => getApiUrl(ENDPOINTS.USERS.PROFILE_IMAGE_URL),
  sidebarPreferences: () => getApiUrl(ENDPOINTS.USERS.SIDEBAR_PREFERENCES),
  fetchOrganizationSettings: () =>
    getApiUrl(ENDPOINTS.USERS.ORGANIZATION_SETTINGS),

  // Notifications
  getNotificationSettings: () => getApiUrl(ENDPOINTS.NOTIFICATIONS.SETTINGS),
  updateNotificationSettings: () => getApiUrl(ENDPOINTS.NOTIFICATIONS.SETTINGS),
  getNotifications: (params?: {
    limit?: number;
    offset?: number;
    search?: string;
  }) => getApiUrl(ENDPOINTS.NOTIFICATIONS.BASE, params || {}),
  markNotificationRead: () => getApiUrl(ENDPOINTS.NOTIFICATIONS.MARK_READ),
  deleteNotification: (id: string) =>
    getApiUrl(ENDPOINTS.NOTIFICATIONS.BY_ID(id)),

  // Relationship assessment (live)
  relationshipAssessmentStakeholders: (accountId?: string) =>
    getApiUrl(
      ENDPOINTS.RELATIONSHIP_ASSESSMENT.STAKEHOLDERS,
      accountId ? { account_id: accountId } : {}
    ),
  relationshipAssessmentAccounts: () =>
    getApiUrl(ENDPOINTS.RELATIONSHIP_ASSESSMENT.ACCOUNTS),
  relationshipAssessmentAccountOverview: (accountId: string) =>
    getApiUrl(ENDPOINTS.RELATIONSHIP_ASSESSMENT.ACCOUNT_OVERVIEW(accountId)),
  relationshipAssessmentAccountAssessments: (accountId: string) =>
    getApiUrl(ENDPOINTS.RELATIONSHIP_ASSESSMENT.ACCOUNT_ASSESSMENTS(accountId)),
  relationshipAssessmentUserMappings: () =>
    getApiUrl(ENDPOINTS.RELATIONSHIP_ASSESSMENT.USER_MAPPINGS),
  relationshipAssessmentResolveInherited: (
    accountId: string,
    userId: string,
    contactId: string
  ) =>
    getApiUrl(
      ENDPOINTS.RELATIONSHIP_ASSESSMENT.RESOLVE_INHERITED(
        accountId,
        userId,
        contactId
      )
    ),

  teamViewsRoster: () => getApiUrl(ENDPOINTS.TEAM_VIEWS.ROSTER),
  teamViewsRoleGaps: () => getApiUrl(ENDPOINTS.TEAM_VIEWS.ROLE_GAPS),
  portfolioCoverage: () => getApiUrl(ENDPOINTS.TEAM_VIEWS.PORTFOLIO_COVERAGE),
  teamViewsHistory: (personId: string) =>
    getApiUrl(ENDPOINTS.TEAM_VIEWS.HISTORY(personId)),
  teamViewsReassignPreview: () =>
    getApiUrl(ENDPOINTS.TEAM_VIEWS.REASSIGN_PREVIEW),
  teamViewsReassignApply: () => getApiUrl(ENDPOINTS.TEAM_VIEWS.REASSIGN_APPLY),
  departureImpactApply: () =>
    getApiUrl(ENDPOINTS.TEAM_VIEWS.DEPARTURE_IMPACT_APPLY),
  coverageDepartedRows: (accountId: string) =>
    getApiUrl(ENDPOINTS.COVERAGE.DEPARTED_ROWS(accountId)),
  coverageSuccessorCandidates: (accountId: string, departingUserId: string) =>
    getApiUrl(
      ENDPOINTS.COVERAGE.SUCCESSOR_CANDIDATES(accountId, departingUserId)
    ),
  coverageDormantHistory: (accountId: string) =>
    getApiUrl(ENDPOINTS.COVERAGE.DORMANT_HISTORY(accountId)),
  coverageTransfer: (accountId: string) =>
    getApiUrl(ENDPOINTS.COVERAGE.TRANSFER(accountId)),
  coverageDismiss: (accountId: string) =>
    getApiUrl(ENDPOINTS.COVERAGE.DISMISS(accountId)),
  coverageMappingPreview: (accountId: string) =>
    getApiUrl(ENDPOINTS.COVERAGE.MAPPING_PREVIEW(accountId)),
  coverageMappingApply: (accountId: string) =>
    getApiUrl(ENDPOINTS.COVERAGE.MAPPING_APPLY(accountId)),

  // Upload
  uploadImage: () => getApiUrl(ENDPOINTS.UPLOAD.IMAGE),
  bulkUpload: () => getApiUrl(ENDPOINTS.UPLOAD.BULK),

  // Approval Requests
  getApprovalRequests: (params?: { upload_type?: string }) =>
    getApiUrl(ENDPOINTS.APPROVAL_REQUESTS.REQUEST, params || {}),
  approveOrRejectRequest: () => getApiUrl(ENDPOINTS.APPROVAL_REQUESTS.APPROVE),
  getUploadStats: () => getApiUrl(ENDPOINTS.UPLOAD_STATS),
  // Command Center
  commandCenterMetrics: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.OPS.COMMAND_CENTER_METRICS, params),
  sponsorEngagementConfig: () => getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.CONFIG),
  sponsorEngagementDiscoveryHub: () =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.DISCOVERY_HUB),
  sponsorEngagementCycles: () => getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.CYCLES),
  sponsorEngagementCycleResponses: (
    cycleId: string,
    params?: { sponsorId?: string; teamMemberId?: string }
  ) => {
    const queryParams: Record<string, string> = {};
    if (params?.sponsorId) {
      queryParams.sponsor_id = params.sponsorId;
    }
    if (params?.teamMemberId) {
      queryParams.team_member_id = params.teamMemberId;
    }
    return getApiUrl(
      ENDPOINTS.SPONSOR_ENGAGEMENT.CYCLE_RESPONSES(cycleId),
      queryParams
    );
  },
  sponsorEngagementCycleLeaderboard: (cycleId: string) =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.CYCLE_LEADERBOARD(cycleId)),
  sponsorEngagementPortfolio: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.PORTFOLIO, params || {}),
  sponsorEngagementMyPerformance: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.MY_PERFORMANCE, params || {}),
  sponsorEngagementLeaderboard: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.LEADERBOARD, params || {}),
  sponsorEngagementTeamFeedback: () =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.TEAM_FEEDBACK),
  sponsorEngagementExecutiveSummary: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.EXECUTIVE_SUMMARY, params || {}),
  sponsorEngagementLaunch: () => getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.LAUNCH),
  sponsorEngagementExtendDueDate: (cycleId: string) =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.EXTEND_DUE_DATE(cycleId)),
  sponsorEngagementLaunchContext: (params?: Record<string, string>) =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.LAUNCH_CONTEXT, params || {}),
  sponsorEngagementSaveDraftLeaderboard: (params?: Record<string, any>) =>
    getApiUrl(
      ENDPOINTS.SPONSOR_ENGAGEMENT.SAVE_DRAFT_LEADERBOARD,
      params || {}
    ),
  sponsorEngagementSubmitApproval: () =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.SUBMIT_APPROVAL),
  sponsorEngagementChangeApprover: () =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.CHANGE_APPROVER),
  sponsorEngagementApprove: () =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.APPROVE),
  sponsorEngagementSendBack: () =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.SEND_BACK),
  sponsorEngagementPutConfig: () =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.CONFIG),
  xfactorBriefingsOrgs: () => getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.ORGS),
  xfactorWatchListBriefing: () =>
    getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.WATCH_LIST_BRIEFING),
  xfactorAccountBriefings: () =>
    getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.ACCOUNT_BRIEFINGS),
  xfactorAccountPipelineStatus: () =>
    getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.ACCOUNT_PIPELINE_STATUS),
  xfactorBatchPipeline: (batchId: string) =>
    getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.BATCH_PIPELINE(batchId)),
  xfactorBatchRetryPipeline: (batchId: string) =>
    getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.BATCH_RETRY_PIPELINE(batchId)),
  xfactorBriefingDigests: () => getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.DIGESTS),
  xfactorKpiAccountBriefings: () =>
    getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.KPI_ACCOUNT_BRIEFINGS),
  xfactorKpiGenerate: () => getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.KPI_GENERATE),
  xfactorKpiAvailability: () =>
    getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.KPI_AVAILABILITY),
  xfactorKpiMetricsBreakdown: () =>
    getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.KPI_METRICS_BREAKDOWN),
  xfactorKpiScoresLatest: () =>
    getApiUrl(ENDPOINTS.XFACTOR_BRIEFINGS.KPI_SCORES_LATEST),
  dataHubRoConfig: () => getApiUrl(ENDPOINTS.METRICS.RO_CONFIG),
  dataHubRoConfigReset: () => getApiUrl(ENDPOINTS.METRICS.RO_CONFIG_RESET),
  sponsorEngagementPoll1: (
    accountId: string,
    params?: Record<string, string>
  ) => getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.POLL1(accountId), params || {}),
  sponsorEngagementPoll1Draft: (
    accountId: string,
    params?: Record<string, string>
  ) =>
    getApiUrl(
      ENDPOINTS.SPONSOR_ENGAGEMENT.POLL1_DRAFT(accountId),
      params || {}
    ),
  sponsorEngagementPoll2: (
    accountId: string,
    params?: Record<string, string>
  ) => getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.POLL2(accountId), params || {}),
  sponsorEngagementPoll2Draft: (
    accountId: string,
    params?: Record<string, string>
  ) =>
    getApiUrl(
      ENDPOINTS.SPONSOR_ENGAGEMENT.POLL2_DRAFT(accountId),
      params || {}
    ),
  sponsorEngagementDrilldown: (
    sponsorId: string,
    params?: Record<string, any>
  ) =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.DRILLDOWN(sponsorId), params || {}),
  sponsorEngagementReminder: (
    sponsorId: string,
    params?: Record<string, unknown>
  ) =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.REMINDER(sponsorId), params || {}),
  sponsorEngagementPoll2Reminder: (
    teamMemberId: string,
    params?: Record<string, unknown>
  ) =>
    getApiUrl(
      ENDPOINTS.SPONSOR_ENGAGEMENT.POLL2_REMINDER(teamMemberId),
      params || {}
    ),
  sponsorEngagementBulkReminders: () =>
    getApiUrl(ENDPOINTS.SPONSOR_ENGAGEMENT.BULK_REMINDERS),
  commandCenterAccountsNeedSponsors: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.OPS.COMMAND_CENTER_ACCOUNTS_NEED_SPONSORS, params),
  commandCenterSponsorsOnBench: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.OPS.COMMAND_CENTER_SPONSORS_ON_BENCH, params),
  commandCenterAccountSurveys: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.OPS.COMMAND_CENTER_ACCOUNT_SURVEYS, params),
  metricsImportCsv: () => getApiUrl(ENDPOINTS.METRICS.IMPORT_CSV),
  metricsImportCsvValidate: () =>
    getApiUrl(ENDPOINTS.METRICS.IMPORT_CSV_VALIDATE),
  metricsStagingProcess: (batchId: string | number) =>
    getApiUrl(ENDPOINTS.METRICS.STAGING_PROCESS, { batch_id: batchId }),
  metricsStagingTransfer: (params?: {
    batch_id?: string | number;
    organization_id?: string;
    organization_name?: string;
    clear_staging?: boolean;
    exclude_unadjudicated?: boolean;
  }) => getApiUrl(ENDPOINTS.METRICS.STAGING_TRANSFER, params || {}),
  metricsStagingCount: () => getApiUrl(ENDPOINTS.METRICS.STAGING_COUNT),
  // Data Hub run bookkeeping
  dataHubStats: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_STATS),
  dataHubDatasets: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_DATASETS),
  dataHubFiles: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_FILES),
  dataHubLedgerRuns: (params?: {
    dataset?: string;
    limit?: number;
    offset?: number;
  }) => getApiUrl(ENDPOINTS.METRICS.DATAHUB_LEDGER_RUNS, params || {}),
  dataHubLedgerRun: (runId: string) =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_LEDGER_RUN(runId)),
  dataHubLedgerDecisions: (runId: string) =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_LEDGER_DECISIONS(runId)),
  dataHubLedgerKill: (runId: string) =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_LEDGER_KILL(runId)),
  dataHubLedgerStepBegin: (runId: string, step: string) =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_LEDGER_STEP_BEGIN(runId, step)),
  dataHubLedgerStepSucceed: (runId: string, step: string) =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_LEDGER_STEP_SUCCEED(runId, step)),
  dataHubReport: (
    key: string,
    params?: {
      run_id?: string | null;
      batch_id?: number | null;
      q?: string | null;
      page?: number;
      page_size?: number;
    }
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params ?? {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return `${getApiUrl(ENDPOINTS.METRICS.DATAHUB_REPORTS)}/${key}${suffix}`;
  },
  dataHubReportCsv: (
    key: string,
    params?: {
      run_id?: string | null;
      batch_id?: number | null;
      q?: string | null;
    }
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params ?? {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return `${getApiUrl(ENDPOINTS.METRICS.DATAHUB_REPORTS)}/${key}/export.csv${suffix}`;
  },
  dataHubFileDownload: (storagePath: string) =>
    `${getApiUrl(ENDPOINTS.METRICS.DATAHUB_FILE_DOWNLOAD)}?storage_path=${encodeURIComponent(storagePath)}`,
  dataHubStagingRows: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_STAGING_ROWS),
  dataHubAccounts: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_ACCOUNTS),
  dataHubMetricDefinitions: () =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_METRIC_DEFINITIONS),
  dataHubQcFileReport: (params?: { run_id?: string; batch_id?: number }) =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_QC_FILE_REPORT, params || {}),
  dataHubQa: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_QA),
  dataHubImportTemplate: () =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_IMPORT_TEMPLATE),
  dataHubRejectedRowsCsv: (params?: { batch_id?: number; run_id?: string }) =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_REJECTED_ROWS_CSV, params || {}),
  dataHubResetTargets: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_RESET_TARGETS),
  dataHubResetPreview: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_RESET_PREVIEW),
  dataHubReset: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_RESET),
  dataHubProductionRows: () =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_PRODUCTION_ROWS),
  dataHubBenchmarks: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_BENCHMARKS),
  dataHubMatrix: (params: {
    kind: string;
    period_type?: string;
    axis?: string;
  }) => getApiUrl(ENDPOINTS.METRICS.DATAHUB_MATRIX, params),
  dataHubCategoryScores: (params?: { persist?: boolean }) =>
    getApiUrl(ENDPOINTS.METRICS.SCORES_CATEGORY, params || { persist: false }),
  dataHubRoCompute: (params?: { persist?: boolean }) =>
    getApiUrl(ENDPOINTS.METRICS.RO_COMPUTE, params || { persist: false }),
  dataHubQcSummary: (params?: { run_id?: string; batch_id?: number }) =>
    getApiUrl(ENDPOINTS.METRICS.QC_SUMMARY, params || {}),
  dataHubQcIssues: () => getApiUrl(ENDPOINTS.METRICS.QC_ISSUES),
  dataHubQcResolve: () => getApiUrl(ENDPOINTS.METRICS.QC_RESOLVE),
  dataHubCustomMetrics: () =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_CUSTOM_METRICS),
  dataHubCustomCategories: () =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_CUSTOM_CATEGORIES),
  dataHubCustomMetric: (metricId: string) =>
    `${getApiUrl(ENDPOINTS.METRICS.DATAHUB_CUSTOM_METRICS)}/${encodeURIComponent(metricId)}`,
  dataHubCustomCategory: (key: string) =>
    `${getApiUrl(ENDPOINTS.METRICS.DATAHUB_CUSTOM_CATEGORIES)}/${encodeURIComponent(key)}`,
  dataHubCustomRubric: () => getApiUrl(ENDPOINTS.METRICS.DATAHUB_CUSTOM_RUBRIC),
  dataHubCustomRubricImport: () =>
    getApiUrl(ENDPOINTS.METRICS.DATAHUB_CUSTOM_RUBRIC_IMPORT),
  dataHubQcConflicts: (params?: { run_id?: string; batch_id?: number }) =>
    getApiUrl(ENDPOINTS.METRICS.QC_CONFLICTS, params || {}),
  dataHubQcConflictResolve: () =>
    getApiUrl(ENDPOINTS.METRICS.QC_CONFLICTS_RESOLVE),
  dataHubQcAcceptAll: (params?: {
    run_id?: string;
    batch_id?: number;
    probe_id?: string;
    metric_id?: string;
  }) => getApiUrl(ENDPOINTS.METRICS.QC_ACCEPT_ALL, params || {}),
  dataHubQcRejectAll: (params?: {
    run_id?: string;
    batch_id?: number;
    probe_id?: string;
    metric_id?: string;
  }) => getApiUrl(ENDPOINTS.METRICS.QC_REJECT_ALL, params || {}),
  dataHubQcRun: () => getApiUrl(ENDPOINTS.METRICS.QC_RUN),
  dataHubRollup: () => getApiUrl(ENDPOINTS.METRICS.SUMMARIZE_ROLLUP),
  dataHubQcProbes: () => getApiUrl(ENDPOINTS.METRICS.QC_PROBES),
  dataHubRuns: (params?: {
    limit?: number;
    offset?: number;
    dataset?: string;
  }) => getApiUrl(ENDPOINTS.METRICS.PIPELINE_RUNS, params || {}),
  dataHubRun: (runId: string) =>
    getApiUrl(ENDPOINTS.METRICS.PIPELINE_RUN(runId)),
  dataHubRunClose: (runId: string) =>
    getApiUrl(ENDPOINTS.METRICS.PIPELINE_RUN_CLOSE(runId)),
  dataHubRunPhase: (runId: string, phase: string) =>
    getApiUrl(ENDPOINTS.METRICS.PIPELINE_RUN_PHASE(runId, phase)),
  metricsSummaryTransfer: () => getApiUrl(ENDPOINTS.METRICS.SUMMARY_TRANSFER),
  metricsSummaryStatus: () => getApiUrl(ENDPOINTS.METRICS.SUMMARY_STATUS),
  metricsNormalize: (params?: {
    max_records?: number;
    batch_size?: number;
    only_null_normalized?: boolean;
  }) =>
    getApiUrl(ENDPOINTS.METRICS.NORMALIZE, {
      max_records: params?.max_records ?? 10000,
      batch_size: params?.batch_size ?? 1000,
      only_null_normalized: params?.only_null_normalized ?? true,
    }),
  metricsNormalizePendingCount: () =>
    getApiUrl(ENDPOINTS.METRICS.NORMALIZE_PENDING_COUNT),
  metricsOrgConfigurations: () =>
    getApiUrl(ENDPOINTS.METRICS.ORG_CONFIGURATIONS),
  metricsOrgConfigurationsUpdate: (configurationId: string) =>
    getApiUrl(`${ENDPOINTS.METRICS.ORG_CONFIGURATIONS}/${configurationId}`),
  metricsBenchmarksCalculate: (params?: {
    time_window_months?: number;
    min_data_points?: number;
    min_unique_accounts?: number;
  }) =>
    getApiUrl(ENDPOINTS.METRICS.BENCHMARKS_CALCULATE, {
      time_window_months: params?.time_window_months ?? 6,
      min_data_points: params?.min_data_points ?? 3,
      min_unique_accounts: params?.min_unique_accounts ?? 3,
    }),
  metricsBenchmarksPropagate: (params?: { time_window_months?: number }) =>
    getApiUrl(ENDPOINTS.METRICS.BENCHMARKS_PROPAGATE, {
      time_window_months: params?.time_window_months ?? 6,
    }),
  metricsHawkeyeDigestDb: (params?: Record<string, string>) =>
    getApiUrl(ENDPOINTS.METRICS.HAWKEYE_DIGEST_DB, params || {}),
  metricsTimePeriods: () => getApiUrl(ENDPOINTS.METRICS.TIME_PERIODS),
  metricsConfigCoverageAudit: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.METRICS.CONFIG_COVERAGE_AUDIT, params || {}),
  // Assignments
  assignments: () => getApiUrl(ENDPOINTS.ASSIGNMENTS.BASE),

  // Assignments
  getActivationReady: () => getApiUrl(ENDPOINTS.ASSIGNMENTS.ACTIVATION_READY),
  getAssignmentsOverview: (loginStatus?: string) =>
    getApiUrl(
      ENDPOINTS.ASSIGNMENTS.OVERVIEW,
      loginStatus ? { login_status: loginStatus } : {}
    ),
  bulkActivationAction: () =>
    getApiUrl(ENDPOINTS.ASSIGNMENTS.ACTIVATION_ACTION_BULK),
  getUnassignedCustomers: () =>
    getApiUrl(ENDPOINTS.ASSIGNMENTS.UNASSIGNED_CUSTOMERS),
  getRecommendUnified: () => getApiUrl(ENDPOINTS.ASSIGNMENTS.RECOMMEND_UNIFIED),
  getPendingApprovals: () => getApiUrl(ENDPOINTS.ASSIGNMENTS.PENDING_APPROVALS),
  approveOrRejectRequestAssessment: () =>
    getApiUrl(ENDPOINTS.ASSIGNMENTS.APPROVE_REQUEST),
  editAssignmentRequest: () =>
    getApiUrl(ENDPOINTS.ASSIGNMENTS.EDIT_REQUEST, { request_type: "edit" }),
  deleteAssignmentRequest: () =>
    getApiUrl(ENDPOINTS.ASSIGNMENTS.EDIT_REQUEST, { request_type: "delete" }),
  // Assessment Templates
  getAssessmentTemplates: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    questionnaire_type?: "small" | "large";
    category?: string;
    sort_by?: "name" | "created_at" | "updated_at";
    sort_order?: "asc" | "desc";
  }) => getApiUrl(ENDPOINTS.ASSESSMENT_TEMPLATES.BASE, params || {}),
  createAssessmentTemplate: (queryParams?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.ASSESSMENT_TEMPLATES.CREATE, queryParams || {}),
  updateAssessmentTemplate: (id: string) =>
    getApiUrl(ENDPOINTS.ASSESSMENT_TEMPLATES.UPDATE(id)),
  // Assessments
  getAssessments: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?:
      | "active"
      | "completed"
      | "draft"
      | "upcoming"
      | "published"
      | "all-status";
    template_id?: string;
    sort_by?: "name" | "created_at" | "due_on" | "status";
    sort_order?: "asc" | "desc";
  }) => {
    const queryParams: Record<string, any> = {};
    if (params?.page) queryParams.page = params.page;
    if (params?.limit) queryParams.page_size = params.limit; // Map limit to page_size for backend
    if (params?.search) queryParams.search = params.search;
    if (params?.status && params.status !== "all-status") {
      queryParams.status = params.status.toUpperCase(); // Backend expects uppercase
    }
    if (params?.template_id) queryParams.template_id = params.template_id;
    if (params?.sort_by) queryParams.sort_by = params.sort_by;
    if (params?.sort_order) queryParams.sort_order = params.sort_order;
    return getApiUrl(ENDPOINTS.ASSESSMENTS.BASE, queryParams);
  },
  createAssessment: () => getApiUrl(ENDPOINTS.ASSESSMENTS.CREATE),
  getAssessmentAccountsTeamsBatch: () =>
    getApiUrl(ENDPOINTS.ASSESSMENTS.ACCOUNTS_TEAMS_BATCH),
  createSelfAssessment: () => getApiUrl(ENDPOINTS.ASSESSMENTS.SELF),
  getSelfAssessmentLatest: (accountId: string) =>
    getApiUrl(ENDPOINTS.ASSESSMENTS.SELF_LATEST, { account_id: accountId }),
  getSelfAssessmentConfig: () =>
    getApiUrl(ENDPOINTS.SELF_ASSESSMENT_CONFIG.BASE),
  updateSelfAssessmentConfig: (id: string) =>
    getApiUrl(ENDPOINTS.SELF_ASSESSMENT_CONFIG.BY_ID(id)),
  getAssessmentValidation: (accountId: string) =>
    getApiUrl(ENDPOINTS.ASSESSMENTS.GET_ASSESSMENT_VALIDATION, {
      account_id: accountId,
    }),
  listVendorSelfAssessments: (params?: {
    status?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
    page?: number;
    page_size?: number;
  }) => {
    const queryParams: Record<string, string | number> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.search) queryParams.search = params.search;
    if (params?.sort_by) queryParams.sort_by = params.sort_by;
    if (params?.sort_order) queryParams.sort_order = params.sort_order;
    if (typeof params?.page === "number") queryParams.page = params.page;
    if (typeof params?.page_size === "number") {
      queryParams.page_size = params.page_size;
    }
    return getApiUrl(
      ENDPOINTS.ASSESSMENTS.SELF_ASSESSMENTS_VENDOR,
      queryParams
    );
  },
  listSelfAssessments: (params?: {
    status?: string;
    account_id?: string;
    search?: string;
    created_by_me?: boolean;
    page?: number;
    page_size?: number;
  }) => {
    const queryParams: Record<string, string | number | boolean> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.account_id) queryParams.account_id = params.account_id;
    if (params?.search) queryParams.search = params.search;
    if (typeof params?.created_by_me === "boolean") {
      queryParams.created_by_me = params.created_by_me;
    }
    if (typeof params?.page === "number") queryParams.page = params.page;
    if (typeof params?.page_size === "number") {
      queryParams.page_size = params.page_size;
    }
    return getApiUrl(ENDPOINTS.ASSESSMENTS.SELF, queryParams);
  },
  updateAssessment: (id: string) => getApiUrl(ENDPOINTS.ASSESSMENTS.UPDATE(id)),
  updateAssessmentStatus: (id: string) =>
    getApiUrl(ENDPOINTS.ASSESSMENTS.UPDATE_STATUS(id)),
  publishAssessmentAccounts: (id: string) =>
    getApiUrl(ENDPOINTS.ASSESSMENTS.PUBLISH(id)),
  bulkUpdateAssessmentStatus: () =>
    getApiUrl(ENDPOINTS.ASSESSMENTS.BULK_UPDATE_STATUS),
  checkSelfRunning: () => getApiUrl(ENDPOINTS.ASSESSMENTS.CHECK_SELF_RUNNING),
  getAssessmentCalendarStatus: () =>
    getApiUrl(ENDPOINTS.ASSESSMENTS.CALENDAR_STATUS),
  checkFormalRunning: () =>
    getApiUrl(ENDPOINTS.ASSESSMENTS.CHECK_FORMAL_RUNNING),
  mergeSelfAssignments: (assessmentId: string, accountId: string) =>
    getApiUrl(
      `api/v1/assessments/${assessmentId}/accounts/${accountId}/merge-self-assignments`
    ),
  getDisconnectReview: (params?: {
    assessmentId?: string;
    assessmentStatus?: "completed" | "published" | "all";
  }) => {
    const queryParams: Record<string, string> = {};
    if (params?.assessmentId) queryParams.assessmentId = params.assessmentId;
    if (params?.assessmentStatus) {
      queryParams.assessmentStatus = params.assessmentStatus;
    }
    return getApiUrl(ENDPOINTS.ASSESSMENTS.DISCONNECT_REVIEW, queryParams);
  },
  triggerDisconnect: () => getApiUrl(ENDPOINTS.ASSESSMENTS.TRIGGER_DISCONNECT),
  getDisconnectResolutions: (params?: {
    status?: "pending_resolution" | "resolved" | "published";
    assessmentId?: string;
    accountId?: string;
    accountName?: string;
  }) => {
    const queryParams: Record<string, string> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.assessmentId) queryParams.assessmentId = params.assessmentId;
    if (params?.accountId) queryParams.accountId = params.accountId;
    if (params?.accountName) queryParams.accountName = params.accountName;
    return getApiUrl(ENDPOINTS.ASSESSMENTS.DISCONNECT_RESOLUTIONS, queryParams);
  },
  recordResolution: () => getApiUrl(ENDPOINTS.ASSESSMENTS.RECORD_RESOLUTION),
  publishPostResolution: () =>
    getApiUrl(ENDPOINTS.ASSESSMENTS.PUBLISH_POST_RESOLUTION),
  deleteAssessment: (id: string) => getApiUrl(ENDPOINTS.ASSESSMENTS.DELETE(id)),
  getAssessmentResponses: (
    id: string,
    params?: {
      account_id?: string;
      page?: number;
      limit?: number;
      is_draft?: boolean;
    }
  ) => {
    const queryParams: Record<string, any> = {
      assessment_id: id,
      ...(params?.account_id ? { account_id: params.account_id } : {}),
      ...(typeof params?.page === "number" ? { page: params.page } : {}),
      ...(typeof params?.limit === "number" ? { page_size: params.limit } : {}),
      ...(typeof params?.is_draft === "boolean"
        ? { is_draft: params.is_draft }
        : {}),
    };
    return getApiUrl(ENDPOINTS.ASSESSMENTS.RESPONSES, queryParams);
  },
  getAssessmentQuestionsAndResponses: (id: string) =>
    getApiUrl(ENDPOINTS.ASSESSMENTS.QUESTIONS_AND_RESPONSES(id)),
  overrideAccountLevel: (assessmentId: string, accountId: string) =>
    getApiUrl(
      ENDPOINTS.ASSESSMENTS.OVERRIDE_ACCOUNT_LEVEL(assessmentId, accountId)
    ),
  getLevelMix: (params?: {
    startDate?: string;
    endDate?: string;
    assessmentId?: string;
    executiveId?: string;
    scope?: string;
  }) => getApiUrl(ENDPOINTS.ASSESSMENTS.LEVEL_MIX, params || {}),
  getAssessmentTrendline: (
    accountId: string,
    params: { from_date: string; to_date: string }
  ) => getApiUrl(ENDPOINTS.ASSESSMENTS.TRENDLINE(accountId), params),

  // Admin
  fetchAdminStatistics: () => getApiUrl(ENDPOINTS.ADMIN.STATISTICS),
  fetchAdminOrganizations: () => getApiUrl(ENDPOINTS.ADMIN.ALL_ORGANIZATIONS),
  adminMetricsDefinitions: () =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_DEFINITIONS),
  adminMetricsDefinition: (metricId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_DEFINITION(metricId)),
  adminMetricsCategories: () =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_CATEGORIES),
  adminMetricsWeightage: () =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_WEIGHTAGE),
  adminMetricsWeightageItem: (configId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_WEIGHTAGE_ITEM(configId)),
  adminMetricsWeightageActivate: () =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_WEIGHTAGE_ACTIVATE),
  adminMetricsReset: () => getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_RESET),
  adminMetricsDatasets: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_DATASETS, params || {}),
  adminMetricsRuns: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_RUNS, params || {}),
  adminMetricsRun: (runId: string, params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_RUN(runId), params || {}),
  adminMetricsQaChecks: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_QA_CHECKS, params || {}),
  adminMetricsQaCheck: (checkKey: string, params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_QA_CHECK(checkKey), params || {}),
  adminMetricsQaRun: () => getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_QA_RUN),
  adminMetricsQaRuns: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.ADMIN.METRICS_CONFIG_QA_RUNS, params || {}),
  adminMetricsQaRunDetail: (runId: string, params?: Record<string, any>) =>
    getApiUrl(
      ENDPOINTS.ADMIN.METRICS_CONFIG_QA_RUN_DETAIL(runId),
      params || {}
    ),
  fetchOrgTelemetrics: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.ORG_TELEMETRICS.BASE, params || {}),
  fetchOrgTelemetricUsers: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.ORG_TELEMETRICS.USERS, params || {}),

  // Signals (Account Portfolio Intelligence)
  getSignalFeed: () => getApiUrl(ENDPOINTS.SIGNALS.FEED),
  getSignalPresentation: () => getApiUrl(ENDPOINTS.SIGNALS.PRESENTATION),
  snoozeSignalFiring: (firingId: string) =>
    getApiUrl(ENDPOINTS.SIGNALS.SNOOZE(firingId)),
  dismissSignalFiring: (firingId: string) =>
    getApiUrl(ENDPOINTS.SIGNALS.DISMISS(firingId)),
  recordSignalGuideStep: (firingId: string) =>
    getApiUrl(ENDPOINTS.SIGNALS.GUIDE_STEP(firingId)),
  recordSignalCtaClick: (firingId: string) =>
    getApiUrl(ENDPOINTS.SIGNALS.CTA_CLICK(firingId)),
  getPortfolioMap: () => getApiUrl(ENDPOINTS.SIGNALS.PORTFOLIO_MAP),
  getSignalHistorySummary: () => getApiUrl(ENDPOINTS.SIGNALS.HISTORY_SUMMARY),
  getSignalActionHistory: () => getApiUrl(ENDPOINTS.SIGNALS.ACTION_HISTORY),
  getAccountSignalHistory: (accountId: string) =>
    getApiUrl(ENDPOINTS.SIGNALS.ACCOUNT_HISTORY(accountId)),
  updateOrganizationRlSettingConfig: (tenantId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.ORGANIZATION_RL_SETTING_CONFIG(tenantId)),
  updateOrganizationWeeklyDigestConfig: (tenantId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.ORGANIZATION_WEEKLY_DIGEST(tenantId)),
  updateOrganizationProductTelemetricsConfig: (tenantId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.ORGANIZATION_PRODUCT_TELEMETRICS(tenantId)),
  updateOrganizationCoverageAnalysisConfig: (tenantId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.ORGANIZATION_COVERAGE_ANALYSIS(tenantId)),
  fetchAdminPortfolioMapsConfig: () =>
    getApiUrl(ENDPOINTS.ADMIN.PORTFOLIO_MAPS_CONFIG),
  updateAdminPortfolioMapsConfig: () =>
    getApiUrl(ENDPOINTS.ADMIN.PORTFOLIO_MAPS_CONFIG),
  fetchPortfolioMapsConfig: () =>
    getApiUrl(ENDPOINTS.USERS.PORTFOLIO_MAPS_CONFIG),
  fetchReleaseNoteVersions: (search?: string) =>
    getApiUrl(
      ENDPOINTS.ADMIN.RELEASE_NOTES,
      search?.trim() ? { q: search.trim() } : {}
    ),
  createReleaseNoteVersion: () => getApiUrl(ENDPOINTS.ADMIN.RELEASE_NOTES),
  updateReleaseNoteVersion: (versionId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.RELEASE_NOTES_VERSION(versionId)),
  deleteReleaseNoteVersion: (versionId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.RELEASE_NOTES_VERSION(versionId)),
  fetchReleaseNotesRecipients: () =>
    getApiUrl(ENDPOINTS.ADMIN.RELEASE_NOTES_RECIPIENTS),
  previewReleaseNotesEmail: () =>
    getApiUrl(ENDPOINTS.ADMIN.RELEASE_NOTES_PREVIEW),
  sendReleaseNotesEmail: () => getApiUrl(ENDPOINTS.ADMIN.RELEASE_NOTES_SEND),
  fetchAdminCronSettings: () => getApiUrl(ENDPOINTS.ADMIN.CRON_SETTINGS),
  updateAdminCronSettings: () => getApiUrl(ENDPOINTS.ADMIN.CRON_SETTINGS),
  adminAlAssessmentDefaults: () =>
    getApiUrl(ENDPOINTS.ADMIN.AL_ASSESSMENT_DEFAULTS),
  adminAlAssessmentTestMode: () =>
    getApiUrl(ENDPOINTS.ADMIN.AL_ASSESSMENT_TEST_MODE),
  adminAlAssessmentTestModeRun: () =>
    getApiUrl(ENDPOINTS.ADMIN.AL_ASSESSMENT_TEST_MODE_RUN),
  adminAlAssessmentTestModeReset: () =>
    getApiUrl(ENDPOINTS.ADMIN.AL_ASSESSMENT_TEST_MODE_RESET),
  fetchSignalAdminOrganizations: () =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_ORGANIZATIONS),
  fetchSignalPipelineHealth: () =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_PIPELINE_HEALTH),
  fetchSignalPipelineStatus: () =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_PIPELINE_STATUS),
  runSignalSweep: () => getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_RUN_SWEEP),
  signalAdminPlatformConfig: () =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_PLATFORM_CONFIG),
  previewSignalAdminPlatformConfig: () =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_PLATFORM_CONFIG_PREVIEW),
  saveSignalAdminActionPlanReviews: () =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_ACTION_PLAN_REVIEWS),
  fetchSignalAdminPlatformAudit: () =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_PLATFORM_AUDIT),
  fetchSignalAdminPlatformRegistry: () =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_PLATFORM_REGISTRY),
  restoreSignalAdminVersion: () =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_RESTORE),
  previewSignalAdminRestore: (version: number) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_RESTORE_PREVIEW(version)),
  signalAdminConfig: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_CONFIG(orgId)),
  fetchSignalAdminAudit: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_AUDIT(orgId)),
  fetchSignalAdminOverview: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_OVERVIEW(orgId)),
  signalAdminOrgPresentation: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_PRESENTATION(orgId)),
  previewSignalAdminOrgPresentation: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_PRESENTATION_PREVIEW(orgId)),
  clearSignalAdminOrgPresentation: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_PRESENTATION_CLEAR(orgId)),
  previewSignalAdminConfig: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_CONFIG_PREVIEW(orgId)),
  fetchSignalAdminRegistry: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_REGISTRY(orgId)),
  fetchSignalAdminTraces: (orgId: string, params?: Record<string, string>) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_TRACES(orgId), params),
  fetchSignalAdminActionHistory: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_ACTION_HISTORY(orgId)),
  fetchSignalAdminRuns: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_RUNS(orgId)),
  signalAdminClassifierImpact: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_CLASSIFIER_IMPACT(orgId)),
  signalAdminClassifierTry: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_CLASSIFIER_TRY(orgId)),
  fetchSignalAdminRunDiff: (orgId: string, params?: Record<string, string>) =>
    getApiUrl(ENDPOINTS.ADMIN.SIGNAL_ADMIN_RUN_DIFF(orgId), params),
  fetchAdminUsers: () => getApiUrl(ENDPOINTS.ADMIN.ALL_USERS),
  fetchAdminActiveUsers: (status: string) =>
    getApiUrl(ENDPOINTS.ADMIN.ACTIVE_USERS(status)),
  createTenant: () => getApiUrl(ENDPOINTS.ADMIN.CREATE_TENANT),
  deleteTenant: (tenantId: string) =>
    getApiUrl(`${ENDPOINTS.ADMIN.BASE}/tenant/${tenantId}`),
  fetchOrganizationLevels: () =>
    getApiUrl(`${ENDPOINTS.ADMIN.BASE}/tenant-levels`),
  createOrganizationLevel: () =>
    getApiUrl(`${ENDPOINTS.ADMIN.BASE}/tenant-levels`),
  updateOrganizationLevel: (id: string) =>
    getApiUrl(`${ENDPOINTS.ADMIN.BASE}/tenant-levels/${id}`),
  deleteOrganizationLevel: (id: string) =>
    getApiUrl(`${ENDPOINTS.ADMIN.BASE}/tenant-levels/${id}`),
  fetchRelationshipLevels: () => getApiUrl(ENDPOINTS.ADMIN.RELATIONSHIP_LEVELS),
  updateRelationshipLevel: (id: string) =>
    getApiUrl(ENDPOINTS.ADMIN.RELATIONSHIP_LEVEL_BY_ID(id)),
  deleteAdminUser: () => getApiUrl(`${ENDPOINTS.ADMIN.BASE}/users/delete`),
  updateAdminUser: () => getApiUrl(`${ENDPOINTS.ADMIN.BASE}/users/update`),
  resetUserPassword: () =>
    getApiUrl(`${ENDPOINTS.ADMIN.BASE}/users/change-password`),
  changeMyPassword: () => getApiUrl(ENDPOINTS.ADMIN.CHANGE_MY_PASSWORD),
  createAdminUser: () => getApiUrl(`${ENDPOINTS.ADMIN.BASE}/users/create`),
  fetchAdminAssessmentTemplates: () =>
    getApiUrl(ENDPOINTS.ADMIN.ASSESSMENT_TEMPLATES),
  createAdminAssessmentTemplate: () =>
    getApiUrl(ENDPOINTS.ADMIN.ASSESSMENT_TEMPLATES, { is_global: true }),
  updateAdminAssessmentTemplate: (id: string) =>
    getApiUrl(`${ENDPOINTS.ADMIN.ASSESSMENT_TEMPLATES}/${id}`),
  fetchAuthLogs: (params?: {
    event_type?: string;
    days?: number;
    page?: number;
    limit?: number;
  }) => {
    const queryParams: Record<string, any> = {};
    if (params?.event_type) queryParams.event_type = params.event_type;
    if (params?.days) queryParams.days = params.days;
    if (params?.page) queryParams.page = params.page;
    if (params?.limit) queryParams.limit = params.limit;
    return getApiUrl(ENDPOINTS.ADMIN.AUTH_LOGS, queryParams);
  },
  sendAdminEmail: () => getApiUrl(ENDPOINTS.ADMIN.SEND_EMAIL),
  healthCheck: () => getApiUrl(ENDPOINTS.ADMIN.HEALTH_CHECK),
  healthCheckRepair: () => getApiUrl(ENDPOINTS.ADMIN.HEALTH_CHECK_REPAIR),
  ibHealthCheck: () => getApiUrl(ENDPOINTS.ADMIN.IB_HEALTH_CHECK),
  ibHealthCheckStatus: () => getApiUrl(ENDPOINTS.ADMIN.IB_HEALTH_CHECK),
  ibHealthCheckRepair: () => getApiUrl(ENDPOINTS.ADMIN.IB_HEALTH_CHECK_REPAIR),
  ibHealthCheckRepairStatus: () =>
    getApiUrl(ENDPOINTS.ADMIN.IB_HEALTH_CHECK_REPAIR),
  ibHealthCheckRepairStop: () =>
    getApiUrl(ENDPOINTS.ADMIN.IB_HEALTH_CHECK_REPAIR_STOP),
  businessHealthBriefingsSync: () =>
    getApiUrl(ENDPOINTS.ADMIN.BUSINESS_HEALTH_BRIEFINGS_SYNC),
  businessHealthBriefingsAccounts: (orgId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.BUSINESS_HEALTH_BRIEFINGS_ACCOUNTS, { orgId }),
  dataIntegrityCheck: () => getApiUrl(ENDPOINTS.ADMIN.DATA_INTEGRITY_CHECK),
  fetchAdminMetadataInfo: (params?: Record<string, any>) =>
    getApiUrl(ENDPOINTS.ADMIN.METADATA_INFO, params || {}),

  // Vendor Admin
  fetchVendorAdminStatistics: () =>
    getApiUrl(ENDPOINTS.VENDOR_ADMIN.STATISTICS),
  fetchVendorAdminGrades: () => getApiUrl("api/v1/vendor-admin/grades"),
  updateVendorAdminGrade: (gradeId: string) =>
    getApiUrl(`api/v1/vendor-admin/grades/${gradeId}`),
  fetchVendorAdminAccountTiers: () =>
    getApiUrl("api/v1/vendor-admin/account-tiers"),
  updateVendorAdminAccountTier: (tierId: string) =>
    getApiUrl(`api/v1/vendor-admin/account-tiers/${tierId}`),
  fetchAssignmentRecommendationSettings: () =>
    getApiUrl(ENDPOINTS.VENDOR_ADMIN.ASSIGNMENT_RECOMMENDATIONS),
  updateAssignmentRecommendationSettings: () =>
    getApiUrl(ENDPOINTS.VENDOR_ADMIN.ASSIGNMENT_RECOMMENDATIONS),
  fetchAssignmentRecommendationHistory: () =>
    getApiUrl(ENDPOINTS.VENDOR_ADMIN.ASSIGNMENT_RECOMMENDATIONS_HISTORY),
  fetchVendorAdminUsers: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    role?: string;
  }) => getApiUrl("api/v1/vendor-admin/users", params || {}),
  createVendorAdminUser: () => getApiUrl("api/v1/vendor-admin/users"),
  deleteVendorUser: (userId: string) => getApiUrl(`api/v1/users/${userId}`),
  resetVendorUserPassword: () =>
    getApiUrl("api/v1/vendor-admin/change-password"),
  updateVendorUser: (userId: string) =>
    getApiUrl(`api/v1/vendor-admin/users/${userId}`),
  resendWelcomeEmail: (userId: string) =>
    getApiUrl(`api/v1/users/${userId}/resend-welcome`),
  sendPrepActivationEmail: (userId: string) =>
    getApiUrl(`api/v1/vendor-admin/users/${userId}/send-prep-activation-email`),
  fetchEmailActivity: (params?: {
    page?: number;
    page_size?: number;
    recipient?: string;
    email_type?: string;
    from_date?: string;
    to_date?: string;
  }) => {
    const queryParams: Record<string, string | number> = {
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 25,
    };
    if (params?.recipient?.trim()) {
      queryParams.recipient = params.recipient.trim();
    }
    if (params?.email_type?.trim()) {
      queryParams.email_type = params.email_type.trim();
    }
    if (params?.from_date?.trim()) {
      queryParams.from_date = params.from_date.trim();
    }
    if (params?.to_date?.trim()) {
      queryParams.to_date = params.to_date.trim();
    }
    return getApiUrl("api/v1/vendor-admin/email-activity", queryParams);
  },
  fetchEmailActivityTemplates: () =>
    getApiUrl("api/v1/vendor-admin/email-activity/templates"),

  // Executive
  getReviewAssessments: (params?: {
    executive_id?: string;
    tab?: "assigned" | "draft" | "completed" | "published" | "aborted";
    account_id?: string;
    search?: string;
    is_draft?: boolean;
  }) => {
    const queryParams: Record<string, any> = {};
    if (params?.executive_id) queryParams.executive_id = params.executive_id;
    if (params?.tab) queryParams.tab = params.tab;
    if (params?.account_id) queryParams.account_id = params.account_id;
    if (params?.search) queryParams.search = params.search;
    if (params?.is_draft === true) queryParams.is_draft = true;
    return getApiUrl(ENDPOINTS.EXECUTIVE.REVIEW_ASSESSMENTS, queryParams);
  },
  submitReviewAssessment: () =>
    getApiUrl(ENDPOINTS.EXECUTIVE.REVIEW_ASSESSMENTS),

  // Color Coding
  fetchColorCodingThresholds: () =>
    getApiUrl(ENDPOINTS.COLOR_CODING.THRESHOLDS),
  updateColorCodingThresholds: (thresholdId: string) =>
    getApiUrl(`${ENDPOINTS.COLOR_CODING.THRESHOLDS}/${thresholdId}`),

  fetchAccountTeamRoleLimits: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.ACCOUNT_TEAM_ROLE_LIMITS),
  fetchEasyOnboardingConfig: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.EASY_ONBOARDING),
  fetchDisconnectFlowConfig: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.DISCONNECT_FLOW),
  fetchPortfolioNeedsAttentionConfig: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.PORTFOLIO_NEEDS_ATTENTION),
  fetchRlSettingConfig: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.RL_SETTING_CONFIG),
  fetchLowScoreCriteriaConfig: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.LOW_SCORE_CRITERIA),
  fetchHealthScoreThresholdsConfig: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.HEALTH_SCORE_THRESHOLDS),
  fetchSignalEngineThresholdsConfig: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.SIGNAL_ENGINE_THRESHOLDS),
  fetchPriorityScoringWeightsConfig: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.PRIORITY_SCORING_WEIGHTS),
  fetchWeeklyDigestConfig: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.WEEKLY_DIGEST),
  fetchWeeklyDigestStatus: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.WEEKLY_DIGEST_STATUS),
  fetchWeeklyDigestSponsors: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.WEEKLY_DIGEST_SPONSORS),
  updateWeeklyDigestSponsors: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.WEEKLY_DIGEST_SPONSORS),
  upsertDisconnectFlowConfig: () => getApiUrl(ENDPOINTS.VENDOR_CONFIG.BASE),
  upsertVendorConfig: () => getApiUrl(ENDPOINTS.VENDOR_CONFIG.BASE),
  sendTestWeeklyDigest: () =>
    getApiUrl(ENDPOINTS.VENDOR_CONFIG.WEEKLY_DIGEST_SEND_TEST),
  fetchInvitations: (params?: { status?: string; limit?: number }) =>
    getApiUrl(ENDPOINTS.INVITATIONS.LIST, params || {}),
  sendOnboardingInvitation: () => getApiUrl(ENDPOINTS.INVITATIONS.SEND),
  sendExistingUserPortfolioInvite: () =>
    getApiUrl(ENDPOINTS.INVITATIONS.SEND_PORTFOLIO),
  sendInvitationReminder: () => getApiUrl(ENDPOINTS.INVITATIONS.REMIND),
  verifyOnboardingWizard: (params: { token: string }) =>
    getApiUrl(ENDPOINTS.ONBOARDING_WIZARD.VERIFY, params),
  fetchOnboardingWizardAccounts: (params: { token: string }) =>
    getApiUrl(ENDPOINTS.ONBOARDING_WIZARD.ACCOUNTS, params),
  completeOnboardingWizard: () =>
    getApiUrl(ENDPOINTS.ONBOARDING_WIZARD.COMPLETE),

  // Videos (Admin)
  fetchAdminVideos: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
  }) => getApiUrl(ENDPOINTS.ADMIN.VIDEO_URLS, params || {}),
  updateAdminVideo: () => getApiUrl(ENDPOINTS.ADMIN.VIDEO_URLS),
  deleteAdminVideo: (id: string) =>
    getApiUrl(ENDPOINTS.ADMIN.VIDEO_URL_BY_ID(id)),
  updateLibraryVideo: (libraryVideoId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.VIDEO_LIBRARY_BY_ID(libraryVideoId)),
  deleteLibraryVideo: (libraryVideoId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.VIDEO_LIBRARY_BY_ID(libraryVideoId)),
  fetchLibraryVideos: () => getApiUrl(ENDPOINTS.ADMIN.VIDEO_LIBRARY),
  createLibraryVideo: () => getApiUrl(ENDPOINTS.ADMIN.VIDEO_LIBRARY),
  reorderLibraryVideos: () => getApiUrl(ENDPOINTS.ADMIN.VIDEO_LIBRARY_REORDER),
  assignLibraryVideos: () => getApiUrl(ENDPOINTS.ADMIN.VIDEO_ASSIGN),
  fetchVideoTopics: () => getApiUrl(ENDPOINTS.ADMIN.VIDEO_TOPICS),
  createVideoTopic: () => getApiUrl(ENDPOINTS.ADMIN.VIDEO_TOPICS),
  reorderVideoTopics: () => getApiUrl(ENDPOINTS.ADMIN.VIDEO_TOPICS),
  updateVideoTopic: (topicId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.VIDEO_TOPIC_BY_ID(topicId)),
  deleteVideoTopic: (topicId: string) =>
    getApiUrl(ENDPOINTS.ADMIN.VIDEO_TOPIC_BY_ID(topicId)),

  // Tips (Admin — Guidance Library)
  fetchAdminTips: (params?: {
    audience?: string;
    category?: string;
    tip_type?: string;
    is_active?: boolean;
    search?: string;
  }) => getApiUrl(ENDPOINTS.ADMIN.SPONSOR_TIPS, params || {}),
  createAdminTip: () => getApiUrl(ENDPOINTS.ADMIN.SPONSOR_TIPS),
  updateAdminTip: (id: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SPONSOR_TIP_BY_ID(id)),
  deleteAdminTip: (id: string) =>
    getApiUrl(ENDPOINTS.ADMIN.SPONSOR_TIP_BY_ID(id)),

  // Videos (User)
  fetchUserVideos: (params?: { role?: string }) =>
    getApiUrl(ENDPOINTS.ADMIN.VIDEO_URLS, params || {}),

  // Guidance Library (Sponsor Tips)
  sponsorTipsList: (
    params: {
      audience?: string;
      category?: string;
      tip_type?: string;
    } = {}
  ) => getApiUrl(ENDPOINTS.SPONSOR_TIPS.BASE, params),
  sponsorTipsRecommended: (params: { limit?: number } = {}) =>
    getApiUrl(ENDPOINTS.SPONSOR_TIPS.RECOMMENDED, params),
  sponsorTipsBookmarks: () => getApiUrl(ENDPOINTS.SPONSOR_TIPS.BOOKMARKS),
  sponsorTipsBookmarkBySlug: (slug: string) =>
    getApiUrl(ENDPOINTS.SPONSOR_TIPS.BOOKMARK_BY_SLUG(slug)),
  sponsorTipBySlug: (slug: string) =>
    getApiUrl(ENDPOINTS.SPONSOR_TIPS.BY_SLUG(slug)),

  // Guidance / Playbook Hub (consumer GETs)
  fetchGuidanceHubSettings: () => getApiUrl(ENDPOINTS.GUIDANCE_HUB.SETTINGS),
  fetchGuidanceNavLinks: (params?: { active_only?: boolean }) =>
    getApiUrl(ENDPOINTS.GUIDANCE_HUB.NAV_LINKS, params || {}),
  fetchGuidancePlaybooks: (role?: string) =>
    getApiUrl(ENDPOINTS.GUIDANCE_HUB.PLAYBOOKS, role ? { role } : undefined),
  fetchGuidancePlaybooksMe: () =>
    getApiUrl(ENDPOINTS.GUIDANCE_HUB.PLAYBOOKS_ME),
  fetchGuidanceRhythmProfiles: () =>
    getApiUrl(ENDPOINTS.GUIDANCE_HUB.RHYTHM_PROFILES),
  fetchGuidanceRhythmCadences: (role?: string) =>
    getApiUrl(
      ENDPOINTS.GUIDANCE_HUB.RHYTHM_CADENCES,
      role ? { role } : undefined
    ),
  fetchGuidanceRhythmBundle: (role: string) =>
    getApiUrl(ENDPOINTS.GUIDANCE_HUB.RHYTHM_BUNDLE(role)),

  // Guidance / Playbook Hub (IzbaAdmin writes — also used for admin list GETs)
  updateGuidanceHubSetting: (key: string) =>
    getApiUrl(ENDPOINTS.ADMIN.GUIDANCE_HUB_SETTINGS(key)),
  createGuidanceNavLink: () =>
    getApiUrl(ENDPOINTS.ADMIN.GUIDANCE_HUB_NAV_LINKS),
  updateGuidanceNavLink: (key: string) =>
    getApiUrl(ENDPOINTS.ADMIN.GUIDANCE_HUB_NAV_LINK_BY_KEY(key)),
  fetchAdminGuidanceNavLinks: () =>
    getApiUrl(ENDPOINTS.ADMIN.GUIDANCE_HUB_NAV_LINKS),
  fetchAdminGuidancePlaybooks: (role?: string) =>
    getApiUrl(
      ENDPOINTS.ADMIN.GUIDANCE_HUB_PLAYBOOKS,
      role ? { role } : undefined
    ),
  updateGuidancePlaybook: (role: string, cadence: string) =>
    getApiUrl(ENDPOINTS.ADMIN.GUIDANCE_HUB_PLAYBOOK_CELL(role, cadence)),
  updateGuidanceRhythmProfile: (role: string) =>
    getApiUrl(ENDPOINTS.ADMIN.GUIDANCE_HUB_RHYTHM_PROFILE(role)),
  updateGuidanceRhythmCadence: (role: string, cadence: string) =>
    getApiUrl(ENDPOINTS.ADMIN.GUIDANCE_HUB_RHYTHM_CADENCE(role, cadence)),
};
